import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageTitle, Card, Button, Badge, Icons, KPI, KPIStrip } from '../shared/ui';

const INPUT_CLS = 'w-full h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600 disabled:bg-stone-50 disabled:text-stone-500';
const LABEL_CLS = 'block text-xs font-medium text-stone-600 mb-1';
const SELECT_CLS = 'w-full h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600';
import api from '../utils/api';
import toast from 'react-hot-toast';
import ServiceConfigManager, { defaultServicesConfig } from '../components/admin/ServiceConfigManager';
import MeterMappingsEditor from '../components/admin/MeterMappingsEditor';
import { propertyService } from '../services/apiService';

const PropertyManagement = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['properties', 'common']);
  const [activeTab, setActiveTab] = useState('properties'); // 'properties', 'catalog', or 'templates'
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showBuildingModal, setShowBuildingModal] = useState(false);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showFurnitureModal, setShowFurnitureModal] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editingFurniture, setEditingFurniture] = useState(null);
  const [showRoomDetail, setShowRoomDetail] = useState(true); // Show room detail panel by default

  // Furniture Template states
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showApplyTemplateModal, setShowApplyTemplateModal] = useState(false);
  const [selectedTemplateForApply, setSelectedTemplateForApply] = useState(null);
  const [templateBuildingId, setTemplateBuildingId] = useState(null); // Building for new template
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    room_type: '',
    building_id: null,
    items: [],
    catalog_items: [], // NEW: catalog-based items [{catalog_item_id, quantity, custom_price}]
    is_active: true
  });
  const [templateItemForm, setTemplateItemForm] = useState({
    name: '',
    description: '',
    quantity: 1,
    default_condition: 'good',
    estimated_price: '',
    category: ''
  });

  // Add Furniture modal tabs & catalog selection
  const [furnitureModalTab, setFurnitureModalTab] = useState('catalog'); // 'catalog' | 'template' | 'manual'
  const [selectedCatalogItems, setSelectedCatalogItems] = useState([]); // [{...catalogItem, quantity, condition}]
  const [catalogSearchText, setCatalogSearchText] = useState('');

  // Furniture Catalog states (per-building)
  const [catalogBuildingId, setCatalogBuildingId] = useState(null);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [editingCatalogItem, setEditingCatalogItem] = useState(null);
  const [catalogForm, setCatalogForm] = useState({
    name: '',
    description: '',
    default_price: '',
    category: '',
    image_url: ''
  });

  // Bulk Room Pricing states
  const [showBulkPricingModal, setShowBulkPricingModal] = useState(false);
  const [bulkPricingForm, setBulkPricingForm] = useState({
    monthly_rent: '',
    apply_to_status: [], // empty = all rooms
    floor: ''  // empty = all floors
  });
  const [selectedRoomIds, setSelectedRoomIds] = useState([]); // For bulk pricing room selection

  // Default utility pricing structure
  const defaultUtilityPricing = {
    electric: { rate: 4000, unit: 'kWh', label: 'Tiền điện' },
    water: { rate: 25000, unit: 'm3', label: 'Tiền nước' },
    internet: { rate: 50000, per: 'person', label: 'Internet' },
    washing: { rate: 75000, per: 'person', label: 'Giặt đồ' },
    elevator: { rate: 50000, per: 'person', label: 'Thang máy' },
    cleaning: { rate: 30000, per: 'person', label: 'Vệ sinh' },
    parking_motorbike: { rate: 100000, per: 'vehicle', label: 'Gửi xe máy' },
    parking_car: { rate: 500000, per: 'vehicle', label: 'Gửi ô tô' }
  };

  // Form states
  const [buildingForm, setBuildingForm] = useState({
    name: '',
    address: '',
    floors: '',
    total_rooms: '',
    rooms_per_floor: [],
    // Room notation configuration
    room_prefix: '',           // e.g., "P", "R", "Phòng", ""
    room_separator: '',        // e.g., "-", ".", ""
    include_floor_in_room: true, // Include floor number in room name
    room_padding: 2,           // Zero padding for room number (1, 2, 3)
    room_start_number: 1,      // Starting room number (0 or 1)
    // Utility Pricing (JSONB) - DEPRECATED: kept for backward compatibility
    utility_pricing: { ...defaultUtilityPricing },
    // NEW: Unified Services Configuration (array-based)
    services_config: [...defaultServicesConfig],
    // Payment method configuration
    payment_method: 'pay_first',  // pay_first or pay_later
    billing_day: null,            // Day of month to bill (null = use contract start day)
    billing_grace_days: 3         // Grace days before payment turns late
  });

  const [roomForm, setRoomForm] = useState({
    room_number: '',
    floor: '',
    area_sqm: '',
    monthly_rent: '',
    status: 'available',
    description: '',
    meter_mappings: {}  // NEW: Physical meter mappings
  });

  const [furnitureForm, setFurnitureForm] = useState({
    name: '',
    description: '',
    quantity: 1,
    condition: 'good',
    purchase_date: '',
    purchase_price: '',
    years_of_use: '',
    image_url: ''
  });

  // Fetch buildings
  const { data: buildings = [], isLoading: buildingsLoading } = useQuery({
    queryKey: ['buildings'],
    queryFn: async () => {
      const response = await api.get('/api/properties/buildings');
      return response.data;
    },
    select: (data) => Array.isArray(data) ? data : []
  });

  // Fetch rooms for selected building
  const { data: rooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ['rooms', selectedBuilding?.id],
    queryFn: async () => {
      if (!selectedBuilding) return [];
      const response = await api.get(`/api/properties/buildings/${selectedBuilding.id}/rooms`);
      return response.data;
    },
    enabled: !!selectedBuilding,
    select: (data) => Array.isArray(data) ? data : []
  });

  // Fetch furniture for selected room (with ticket info)
  const { data: furniture = [], isLoading: furnitureLoading } = useQuery({
    queryKey: ['furniture', selectedRoom?.id],
    queryFn: async () => {
      if (!selectedRoom) return [];
      const response = await api.get(`/api/properties/rooms/${selectedRoom.id}/furniture?include_tickets=true`);
      return response.data;
    },
    enabled: !!selectedRoom,
    select: (data) => Array.isArray(data) ? data : []
  });

  // Fetch room details with contract info - ALWAYS check for active contracts
  const { data: roomDetails, isLoading: roomDetailsLoading } = useQuery({
    queryKey: ['roomDetails', selectedRoom?.id],
    queryFn: async () => {
      if (!selectedRoom) return null;
      try {
        // Fetch room details
        const roomResponse = await api.get(`/api/properties/rooms/${selectedRoom.id}`);
        const roomData = roomResponse.data;

        // ALWAYS try to fetch active contracts for this room (regardless of stored status)
        try {
          const contractsResponse = await api.get(`/api/contracts?room_id=${selectedRoom.id}&status=active`);
          const contracts = contractsResponse.data;
          if (contracts && contracts.length > 0) {
            const activeContract = contracts[0];
            // Room has active contract - it should be occupied
            const actualStatus = 'occupied';

            // Return room data with contract and correct status
            return {
              ...roomData,
              status: actualStatus, // Override status based on contract
              contract: activeContract,
              tenant: activeContract.renters?.[0] || null
            };
          }
        } catch (e) {
          console.log('No active contract found for room:', e.message);
        }

        // No active contract - room status is as stored (available, maintenance, etc.)
        return roomData;
      } catch (error) {
        console.error('Error fetching room details:', error);
        return selectedRoom;
      }
    },
    enabled: !!selectedRoom
  });

  // Fetch furniture templates
  const { data: furnitureTemplates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['furnitureTemplates'],
    queryFn: async () => {
      const response = await api.get('/api/furniture-templates');
      return response.data;
    },
    enabled: activeTab === 'templates' || showFurnitureModal,
    select: (data) => Array.isArray(data) ? data : []
  });

  // Fetch available rooms for applying templates
  const { data: availableRooms = [] } = useQuery({
    queryKey: ['availableRooms'],
    queryFn: async () => {
      const response = await api.get('/api/properties/rooms');
      return response.data;
    },
    enabled: showApplyTemplateModal,
    select: (data) => Array.isArray(data) ? data : []
  });

  // Furniture Template mutations
  const createTemplateMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/furniture-templates', data);
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['furnitureTemplates'] });
      setShowTemplateModal(false);
      resetTemplateForm();
      toast.success(t('properties:toast.templateCreated'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.templateCreateFailed'));
    }
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/api/furniture-templates/${id}`, data);
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['furnitureTemplates'] });
      setShowTemplateModal(false);
      setEditingTemplate(null);
      resetTemplateForm();
      toast.success(t('properties:toast.templateUpdated'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.templateUpdateFailed'));
    }
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/api/furniture-templates/${id}`);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['furnitureTemplates'] });
      toast.success(t('properties:toast.templateDeleted'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.templateDeleteFailed'));
    }
  });

  const applyTemplateMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/furniture-templates/apply', data);
      return response.data;
    },
    onSuccess: async (result) => {
      await queryClient.refetchQueries({ queryKey: ['furniture'] });
      await queryClient.refetchQueries({ queryKey: ['furnitureTemplates'] });
      setShowApplyTemplateModal(false);
      setSelectedTemplateForApply(null);
      toast.success(`Template applied! Created ${result.created_furniture_count} furniture items.`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.templateApplyFailed'));
    }
  });

  // Fetch furniture catalog for selected building (catalog tab)
  const { data: furnitureCatalog = [], isLoading: catalogLoading } = useQuery({
    queryKey: ['furnitureCatalog', catalogBuildingId],
    queryFn: () => propertyService.getFurnitureCatalog(catalogBuildingId),
    enabled: activeTab === 'catalog' && !!catalogBuildingId,
    select: (data) => Array.isArray(data) ? data : []
  });

  // Fetch furniture catalog for the "Add Furniture" modal (uses selected building)
  const { data: modalCatalogItems = [], isLoading: modalCatalogLoading } = useQuery({
    queryKey: ['furnitureCatalog', selectedBuilding?.id],
    queryFn: () => propertyService.getFurnitureCatalog(selectedBuilding?.id),
    enabled: showFurnitureModal && !editingFurniture && !!selectedBuilding?.id,
    select: (data) => Array.isArray(data) ? data : []
  });

  // Fetch furniture catalog for template building (in template modal)
  const { data: templateCatalogItems = [] } = useQuery({
    queryKey: ['furnitureCatalog', templateBuildingId],
    queryFn: () => propertyService.getFurnitureCatalog(templateBuildingId),
    enabled: showTemplateModal && !!templateBuildingId,
    select: (data) => Array.isArray(data) ? data : []
  });

  // Furniture Catalog mutations
  const createCatalogItemMutation = useMutation({
    mutationFn: async (data) => propertyService.createCatalogItem(catalogBuildingId, data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['furnitureCatalog', catalogBuildingId] });
      setShowCatalogModal(false);
      resetCatalogForm();
      toast.success(t('properties:toast.catalogItemCreated'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.catalogItemCreateFailed'));
    }
  });

  const updateCatalogItemMutation = useMutation({
    mutationFn: async ({ id, data }) => propertyService.updateCatalogItem(id, data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['furnitureCatalog', catalogBuildingId] });
      setShowCatalogModal(false);
      setEditingCatalogItem(null);
      resetCatalogForm();
      toast.success(t('properties:toast.catalogItemUpdated'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.catalogItemUpdateFailed'));
    }
  });

  const deleteCatalogItemMutation = useMutation({
    mutationFn: async (id) => propertyService.deleteCatalogItem(id),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['furnitureCatalog', catalogBuildingId] });
      toast.success(t('properties:toast.catalogItemDeleted'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.catalogItemDeleteFailed'));
    }
  });

  // Building mutations
  const createBuildingMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/properties/buildings', data);
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['buildings'] });
      setShowBuildingModal(false);
      resetBuildingForm();
      toast.success(t('properties:toast.buildingCreated'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.buildingCreateFailed'));
    }
  });

  const updateBuildingMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/api/properties/buildings/${id}`, data);
      return response.data;
    },
    onSuccess: async (_, variables) => {
      await queryClient.refetchQueries({ queryKey: ['buildings'] });
      // Also update the selectedBuilding if it was the one being edited
      if (selectedBuilding?.id === variables.id) {
        const updatedBuildings = queryClient.getQueryData(['buildings']);
        const updated = updatedBuildings?.find(b => b.id === variables.id);
        if (updated) setSelectedBuilding(updated);
      }
      setShowBuildingModal(false);
      setEditingBuilding(null);
      resetBuildingForm();
      toast.success(t('properties:toast.buildingUpdated'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.buildingUpdateFailed'));
    }
  });

  const applyBillingPolicyMutation = useMutation({
    mutationFn: async (buildingId) => {
      const response = await api.post(`/api/contracts/apply-building-billing/${buildingId}`);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || t('properties:toast.billingPolicyApplied'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.billingPolicyFailed'));
    }
  });

  const deleteBuildingMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/api/properties/buildings/${id}`);
    },
    onSuccess: async (_, deletedId) => {
      // Clear selection if deleted building was selected
      if (selectedBuilding?.id === deletedId) {
        setSelectedBuilding(null);
        setSelectedRoom(null);
      }
      await queryClient.refetchQueries({ queryKey: ['buildings'] });
      toast.success(t('properties:toast.buildingDeleted'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.buildingDeleteFailed'));
    }
  });

  // Room mutations
  const createRoomMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/properties/rooms', {
        ...data,
        building_id: selectedBuilding.id
      });
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['rooms', selectedBuilding?.id] });
      await queryClient.refetchQueries({ queryKey: ['buildings'] });
      setShowRoomModal(false);
      resetRoomForm();
      toast.success(t('properties:toast.roomCreated'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.roomCreateFailed'));
    }
  });

  const updateRoomMutation = useMutation({
    mutationFn: async ({ id, data, buildingId }) => {
      const response = await api.put(`/api/properties/rooms/${id}`, data);
      return { ...response.data, buildingId };
    },
    onSuccess: async (updatedRoom) => {
      const { buildingId } = updatedRoom;
      // Refetch rooms to ensure UI is updated
      await queryClient.refetchQueries({ queryKey: ['rooms', buildingId] });
      // Update selected room if it was the one being edited
      if (selectedRoom?.id === updatedRoom.id) {
        setSelectedRoom(updatedRoom);
      }
      setShowRoomModal(false);
      setEditingRoom(null);
      resetRoomForm();
      toast.success(t('properties:toast.roomUpdated'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.roomUpdateFailed'));
    }
  });

  const deleteRoomMutation = useMutation({
    mutationFn: async ({ roomId, buildingId }) => {
      const response = await api.delete(`/api/properties/rooms/${roomId}`);
      return { roomId, buildingId, response };
    },
    onSuccess: async (data) => {
      const { roomId, buildingId } = data;
      setSelectedRoom(null);
      // Refetch both rooms and buildings
      await queryClient.refetchQueries({ queryKey: ['rooms', buildingId] });
      await queryClient.refetchQueries({ queryKey: ['buildings'] });
      toast.success(t('properties:toast.roomDeleted'));
    },
    onError: (error, variables) => {
      const errorMessage = error.response?.data?.detail || t('properties:toast.roomDeleteFailed');
      const { roomId, buildingId } = variables;

      if (error.response?.status === 404) {
        toast.info(t('properties:toast.roomNotFound'));
        queryClient.setQueryData(['rooms', buildingId], (oldData) => {
          if (!oldData) return oldData;
          return oldData.filter(room => room.id !== roomId);
        });
        setSelectedRoom(null);
      } else if (error.response?.status === 400) {
        toast.error(errorMessage, { duration: 5000 });
      } else {
        toast.error(errorMessage);
      }
    }
  });

  // Bulk Room Pricing mutation
  const bulkPricingMutation = useMutation({
    mutationFn: async ({ buildingId, data }) => {
      const response = await api.put(`/api/properties/buildings/${buildingId}/rooms/bulk-pricing`, data);
      return response.data;
    },
    onSuccess: async (data) => {
      await queryClient.refetchQueries({ queryKey: ['rooms', selectedBuilding?.id] });
      await queryClient.refetchQueries({ queryKey: ['buildings'] });
      // Don't close modal - allow user to continue with other rooms
      setSelectedRoomIds([]); // Clear selection after applying
      setBulkPricingForm({ ...bulkPricingForm, monthly_rent: '' }); // Clear price but keep other settings
      toast.success(data.message || `Updated ${data.updated_rooms} room(s)`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.roomPricingUpdateFailed'));
    }
  });

  // Furniture mutations
  const createFurnitureMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/api/properties/rooms/${selectedRoom.id}/furniture`, data);
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['furniture', selectedRoom?.id] });
      setShowFurnitureModal(false);
      resetFurnitureForm();
      toast.success(t('properties:toast.furnitureAdded'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.furnitureAddFailed'));
    }
  });

  const updateFurnitureMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/api/furniture/${id}`, data);
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['furniture', selectedRoom?.id] });
      setShowFurnitureModal(false);
      setEditingFurniture(null);
      resetFurnitureForm();
      toast.success(t('properties:toast.furnitureUpdated'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.furnitureUpdateFailed'));
    }
  });

  const deleteFurnitureMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/api/furniture/${id}`);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['furniture', selectedRoom?.id] });
      toast.success(t('properties:toast.furnitureDeleted'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('properties:toast.furnitureDeleteFailed'));
    }
  });

  // State for quick fill
  const [quickFillRooms, setQuickFillRooms] = useState('');

  // Form handlers
  const resetBuildingForm = () => {
    setBuildingForm({
      name: '',
      address: '',
      floors: '',
      total_rooms: '',
      rooms_per_floor: [],
      room_prefix: '',
      room_separator: '',
      include_floor_in_room: true,
      room_padding: 2,
      room_start_number: 1,
      utility_pricing: { ...defaultUtilityPricing },
      services_config: [...defaultServicesConfig],
      payment_method: 'pay_first',
      billing_day: null,
      billing_grace_days: 3
    });
    setQuickFillRooms('');
  };

  // Quick fill all floors with same number of rooms
  const handleQuickFill = () => {
    if (!quickFillRooms || buildingForm.rooms_per_floor.length === 0) return;
    const roomsPerFloor = parseInt(quickFillRooms) || 0;
    const newRoomsPerFloor = buildingForm.rooms_per_floor.map(f => ({
      ...f,
      rooms: roomsPerFloor
    }));
    // Only recalculate total_rooms for new buildings
    if (editingBuilding) {
      setBuildingForm({
        ...buildingForm,
        rooms_per_floor: newRoomsPerFloor
      });
    } else {
      const totalRooms = newRoomsPerFloor.length * roomsPerFloor;
      setBuildingForm({
        ...buildingForm,
        rooms_per_floor: newRoomsPerFloor,
        total_rooms: totalRooms
      });
    }
  };

  // Generate room number based on notation config
  const generateRoomNumber = (floor, roomIndex) => {
    const { room_prefix, room_separator, include_floor_in_room, room_padding, room_start_number } = buildingForm;
    const actualRoomNum = roomIndex + (room_start_number || 0);
    const paddedRoom = String(actualRoomNum).padStart(room_padding || 2, '0');

    let roomNumber = room_prefix || '';
    if (include_floor_in_room) {
      roomNumber += floor;
      roomNumber += room_separator || '';
    }
    roomNumber += paddedRoom;

    return roomNumber;
  };

  // Generate room number preview
  const getRoomNumberPreview = () => {
    const preview = [];
    buildingForm.rooms_per_floor.forEach((floorData) => {
      const numRooms = parseInt(floorData.rooms) || 0;
      if (numRooms > 0) {
        const rooms = [];
        for (let i = 1; i <= Math.min(numRooms, 3); i++) {
          rooms.push(generateRoomNumber(floorData.floor, i));
        }
        if (numRooms > 3) {
          rooms.push('...');
          rooms.push(generateRoomNumber(floorData.floor, numRooms));
        }
        preview.push({ floor: floorData.floor, rooms, total: numRooms });
      }
    });
    return preview;
  };

  // Handle floor count change - generate rooms_per_floor array
  const handleFloorsChange = (floorsValue) => {
    const numFloors = parseInt(floorsValue) || 0;
    const newRoomsPerFloor = Array.from({ length: numFloors }, (_, i) => {
      // Preserve existing values if available
      return buildingForm.rooms_per_floor[i] || { floor: i + 1, rooms: '' };
    });
    // Only recalculate total_rooms for new buildings; when editing, keep existing value
    if (editingBuilding) {
      setBuildingForm({
        ...buildingForm,
        floors: floorsValue,
        rooms_per_floor: newRoomsPerFloor
      });
    } else {
      const totalRooms = newRoomsPerFloor.reduce((sum, f) => sum + (parseInt(f.rooms) || 0), 0);
      setBuildingForm({
        ...buildingForm,
        floors: floorsValue,
        rooms_per_floor: newRoomsPerFloor,
        total_rooms: totalRooms
      });
    }
  };

  // Handle rooms per floor change
  const handleRoomsPerFloorChange = (floorIndex, roomsValue) => {
    const newRoomsPerFloor = [...buildingForm.rooms_per_floor];
    newRoomsPerFloor[floorIndex] = { ...newRoomsPerFloor[floorIndex], rooms: roomsValue };
    // Only recalculate total_rooms for new buildings; when editing, keep existing value
    if (editingBuilding) {
      setBuildingForm({
        ...buildingForm,
        rooms_per_floor: newRoomsPerFloor
      });
    } else {
      const totalRooms = newRoomsPerFloor.reduce((sum, f) => sum + (parseInt(f.rooms) || 0), 0);
      setBuildingForm({
        ...buildingForm,
        rooms_per_floor: newRoomsPerFloor,
        total_rooms: totalRooms
      });
    }
  };

  const resetRoomForm = () => {
    setRoomForm({
      room_number: '', floor: '', area_sqm: '', monthly_rent: '',
      status: 'available', description: ''
    });
  };

  const resetFurnitureForm = () => {
    setFurnitureForm({
      name: '', description: '', quantity: 1, condition: 'good',
      purchase_date: '', purchase_price: '', years_of_use: '', image_url: ''
    });
  };

  const resetTemplateForm = () => {
    setTemplateBuildingId(null);
    setTemplateForm({
      name: '',
      description: '',
      room_type: '',
      building_id: null,
      items: [],
      catalog_items: [],
      is_active: true
    });
    setTemplateItemForm({
      name: '',
      description: '',
      quantity: 1,
      default_condition: 'good',
      estimated_price: '',
      category: ''
    });
  };

  const resetCatalogForm = () => {
    setCatalogForm({
      name: '',
      description: '',
      default_price: '',
      category: '',
      image_url: ''
    });
  };

  const handleEditCatalogItem = (item) => {
    setEditingCatalogItem(item);
    setCatalogForm({
      name: item.name,
      description: item.description || '',
      default_price: item.default_price || '',
      category: item.category || '',
      image_url: item.image_url || ''
    });
    setShowCatalogModal(true);
  };

  const handleSubmitCatalogItem = (e) => {
    e.preventDefault();
    const data = {
      name: catalogForm.name,
      description: catalogForm.description || null,
      default_price: parseFloat(catalogForm.default_price) || 0,
      category: catalogForm.category || null,
      image_url: catalogForm.image_url || null
    };

    if (editingCatalogItem) {
      updateCatalogItemMutation.mutate({ id: editingCatalogItem.id, data });
    } else {
      createCatalogItemMutation.mutate(data);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateBuildingId(template.building_id || null);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      room_type: template.room_type || '',
      building_id: template.building_id || null,
      items: template.items || [],
      catalog_items: template.catalog_items || [],
      is_active: template.is_active
    });
    setShowTemplateModal(true);
  };

  const addTemplateItem = () => {
    if (!templateItemForm.name.trim()) {
      toast.error(t('properties:toast.itemNameRequired'));
      return;
    }
    const newItem = {
      ...templateItemForm,
      quantity: parseInt(templateItemForm.quantity) || 1,
      estimated_price: parseFloat(templateItemForm.estimated_price) || 0
    };
    setTemplateForm({
      ...templateForm,
      items: [...templateForm.items, newItem]
    });
    setTemplateItemForm({
      name: '',
      description: '',
      quantity: 1,
      default_condition: 'good',
      estimated_price: '',
      category: ''
    });
  };

  const removeTemplateItem = (index) => {
    setTemplateForm({
      ...templateForm,
      items: templateForm.items.filter((_, i) => i !== index)
    });
  };

  const handleSubmitTemplate = (e) => {
    e.preventDefault();
    const data = {
      name: templateForm.name,
      description: templateForm.description,
      room_type: templateForm.room_type,
      is_active: templateForm.is_active,
      building_id: templateBuildingId || null,
      // Legacy inline items
      items: templateForm.items.map(item => ({
        ...item,
        quantity: parseInt(item.quantity) || 1,
        estimated_price: parseFloat(item.estimated_price) || 0
      })),
      // New catalog-based items
      catalog_items: templateForm.catalog_items.map(ci => ({
        catalog_item_id: ci.catalog_item_id,
        quantity: parseInt(ci.quantity) || 1,
        custom_price: ci.custom_price ? parseFloat(ci.custom_price) : null
      }))
    };

    if (editingTemplate) {
      updateTemplateMutation.mutate({ id: editingTemplate.id, data });
    } else {
      createTemplateMutation.mutate(data);
    }
  };

  // Add catalog item to template
  const addCatalogItemToTemplate = (catalogItem) => {
    // Check if already added
    const existing = templateForm.catalog_items.find(ci => ci.catalog_item_id === catalogItem.id);
    if (existing) {
      // Increment quantity
      setTemplateForm({
        ...templateForm,
        catalog_items: templateForm.catalog_items.map(ci =>
          ci.catalog_item_id === catalogItem.id
            ? { ...ci, quantity: ci.quantity + 1 }
            : ci
        )
      });
    } else {
      // Add new
      setTemplateForm({
        ...templateForm,
        catalog_items: [
          ...templateForm.catalog_items,
          {
            catalog_item_id: catalogItem.id,
            catalog_item_name: catalogItem.name,
            default_price: catalogItem.default_price,
            quantity: 1,
            custom_price: null
          }
        ]
      });
    }
  };

  // Remove catalog item from template
  const removeCatalogItemFromTemplate = (catalogItemId) => {
    setTemplateForm({
      ...templateForm,
      catalog_items: templateForm.catalog_items.filter(ci => ci.catalog_item_id !== catalogItemId)
    });
  };

  // Update catalog item quantity in template
  const updateCatalogItemQuantity = (catalogItemId, quantity) => {
    setTemplateForm({
      ...templateForm,
      catalog_items: templateForm.catalog_items.map(ci =>
        ci.catalog_item_id === catalogItemId
          ? { ...ci, quantity: parseInt(quantity) || 1 }
          : ci
      )
    });
  };

  const handleApplyTemplate = (roomId) => {
    if (!selectedTemplateForApply || !roomId) return;
    applyTemplateMutation.mutate({
      template_id: selectedTemplateForApply.id,
      room_id: parseInt(roomId)
    });
  };

  const handleEditBuilding = async (building) => {
    setEditingBuilding(building);
    // Fetch actual rooms to compute per-floor counts
    const numFloors = building.floors || 0;
    let buildingRooms = queryClient.getQueryData(['rooms', building.id]);
    if (!buildingRooms || !Array.isArray(buildingRooms) || buildingRooms.length === 0) {
      try {
        const response = await api.get(`/api/properties/buildings/${building.id}/rooms`);
        buildingRooms = Array.isArray(response.data) ? response.data : [];
      } catch {
        buildingRooms = [];
      }
    }
    const floorCounts = {};
    buildingRooms.forEach(room => {
      const floor = room.floor || 1;
      floorCounts[floor] = (floorCounts[floor] || 0) + 1;
    });
    const roomsPerFloor = Array.from({ length: numFloors }, (_, i) => ({
      floor: i + 1,
      rooms: floorCounts[i + 1] || 0
    }));
    // Parse utility_pricing if it's a string (DEPRECATED)
    let utilityPricing = building.utility_pricing || defaultUtilityPricing;
    if (typeof utilityPricing === 'string') {
      try {
        utilityPricing = JSON.parse(utilityPricing);
      } catch {
        utilityPricing = { ...defaultUtilityPricing };
      }
    }
    // Merge with defaults to ensure all keys exist
    utilityPricing = { ...defaultUtilityPricing, ...utilityPricing };

    // NEW: Parse services_config (array-based)
    let servicesConfig = building.services_config || defaultServicesConfig;
    if (typeof servicesConfig === 'string') {
      try {
        servicesConfig = JSON.parse(servicesConfig);
      } catch {
        servicesConfig = [...defaultServicesConfig];
      }
    }
    // Ensure it's an array
    if (!Array.isArray(servicesConfig)) {
      servicesConfig = [...defaultServicesConfig];
    }

    setBuildingForm({
      name: building.name,
      address: building.address,
      floors: building.floors,
      total_rooms: building.total_rooms,
      rooms_per_floor: roomsPerFloor,
      room_prefix: '',
      room_separator: '',
      include_floor_in_room: true,
      room_padding: 2,
      room_start_number: 1,
      utility_pricing: utilityPricing,
      services_config: servicesConfig,
      payment_method: building.payment_method || 'pay_first',
      billing_day: building.billing_day || null,
      billing_grace_days: building.billing_grace_days ?? 3
    });
    setShowBuildingModal(true);
  };

  const handleEditRoom = (room) => {
    // Close the room-detail modal first. Leaving it open stacks a second
    // .modal-backdrop (same z-50) over the edit form and steals its input focus,
    // so fields (incl. rent) can't be typed. Opening the edit modal standalone —
    // like the bulk-pricing modal that works — fixes it.
    setSelectedRoom(null);
    setEditingRoom(room);
    // Parse meter_mappings if it's a string
    let meterMappings = room.meter_mappings || {};
    if (typeof meterMappings === 'string') {
      try {
        meterMappings = JSON.parse(meterMappings);
      } catch {
        meterMappings = {};
      }
    }
    setRoomForm({
      room_number: room.room_number,
      floor: room.floor,
      area_sqm: room.area_sqm || '',
      monthly_rent: room.monthly_rent,
      status: room.status,
      description: room.description || '',
      meter_mappings: meterMappings
    });
    setShowRoomModal(true);
  };

  const handleEditFurniture = (item) => {
    setEditingFurniture(item);
    setFurnitureForm({
      name: item.name,
      description: item.description || '',
      quantity: item.quantity,
      condition: item.condition || 'good',
      purchase_date: item.purchase_date || '',
      purchase_price: item.purchase_price || '',
      years_of_use: item.years_of_use || '',
      image_url: item.image_url || ''
    });
    setShowFurnitureModal(true);
  };

  const handleSubmitBuilding = (e) => {
    e.preventDefault();

    // Format rooms_per_floor data properly for the backend
    const formattedRoomsPerFloor = buildingForm.rooms_per_floor
      .map(f => ({
        floor: parseInt(f.floor) || 0,
        rooms: parseInt(f.rooms) || 0
      }))
      .filter(f => f.rooms > 0); // Only include floors with rooms

    // Room notation configuration
    const roomNotation = {
      prefix: buildingForm.room_prefix || '',
      separator: buildingForm.room_separator || '',
      include_floor: buildingForm.include_floor_in_room !== false,
      padding: buildingForm.room_padding || 2,
      start_number: buildingForm.room_start_number || 1
    };

    const data = {
      name: buildingForm.name,
      address: buildingForm.address,
      floors: parseInt(buildingForm.floors) || 0,
      total_rooms: parseInt(buildingForm.total_rooms) || 0,
      utility_pricing: buildingForm.utility_pricing || defaultUtilityPricing,
      // NEW: Include services_config (array-based unified schema)
      services_config: buildingForm.services_config || defaultServicesConfig,
      // Payment method configuration
      payment_method: buildingForm.payment_method || 'pay_first',
      billing_day: buildingForm.billing_day || null,
      billing_grace_days: parseInt(buildingForm.billing_grace_days) || 3,
      // Only include rooms_per_floor and notation for new buildings (not when editing)
      ...(editingBuilding ? {} : {
        rooms_per_floor: formattedRoomsPerFloor,
        room_notation: roomNotation
      })
    };

    if (editingBuilding) {
      updateBuildingMutation.mutate({ id: editingBuilding.id, data });
    } else {
      createBuildingMutation.mutate(data);
    }
  };

  const handleSubmitRoom = (e) => {
    e.preventDefault();
    const data = editingRoom ? {
      room_number: roomForm.room_number,
      floor: parseInt(roomForm.floor) || 1,
      area_sqm: parseFloat(roomForm.area_sqm) || null,
      monthly_rent: parseFloat(roomForm.monthly_rent) || 0,
      status: roomForm.status,
      meter_mappings: roomForm.meter_mappings || {}  // NEW: Include meter mappings
    } : {
      ...roomForm,
      floor: parseInt(roomForm.floor) || 1,
      area_sqm: parseFloat(roomForm.area_sqm) || null,
      monthly_rent: parseFloat(roomForm.monthly_rent) || 0,
      meter_mappings: roomForm.meter_mappings || {}
    };

    if (editingRoom) {
      updateRoomMutation.mutate({ id: editingRoom.id, data, buildingId: selectedBuilding?.id });
    } else {
      createRoomMutation.mutate(data);
    }
  };

  const handleSubmitFurniture = (e) => {
    e.preventDefault();
    const data = {
      ...furnitureForm,
      quantity: parseInt(furnitureForm.quantity),
      purchase_price: parseFloat(furnitureForm.purchase_price) || null,
      years_of_use: parseInt(furnitureForm.years_of_use) || null
    };

    if (editingFurniture) {
      updateFurnitureMutation.mutate({ id: editingFurniture.id, data });
    } else {
      createFurnitureMutation.mutate(data);
    }
  };

  // Catalog selection helpers for "Add Furniture" modal
  const handleAddCatalogItemToSelection = (catalogItem) => {
    const existing = selectedCatalogItems.find(i => i.id === catalogItem.id);
    if (existing) {
      setSelectedCatalogItems(prev => prev.map(i =>
        i.id === catalogItem.id ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setSelectedCatalogItems(prev => [...prev, {
        ...catalogItem,
        quantity: 1,
        condition: 'good'
      }]);
    }
  };

  const handleRemoveCatalogItemFromSelection = (catalogItemId) => {
    setSelectedCatalogItems(prev => prev.filter(i => i.id !== catalogItemId));
  };

  const handleUpdateSelectedItemQuantity = (catalogItemId, quantity) => {
    setSelectedCatalogItems(prev => prev.map(i =>
      i.id === catalogItemId ? { ...i, quantity: Math.max(1, parseInt(quantity) || 1) } : i
    ));
  };

  const handleUpdateSelectedItemCondition = (catalogItemId, condition) => {
    setSelectedCatalogItems(prev => prev.map(i =>
      i.id === catalogItemId ? { ...i, condition } : i
    ));
  };

  const handleAddFromCatalog = async () => {
    if (selectedCatalogItems.length === 0) {
      toast.error(t('properties:toast.selectAtLeastOneItem'));
      return;
    }
    try {
      for (const item of selectedCatalogItems) {
        await createFurnitureMutation.mutateAsync({
          name: item.name,
          description: item.description || '',
          quantity: item.quantity,
          condition: item.condition || 'good',
          purchase_price: item.default_price || 0,
          purchase_date: new Date().toISOString().split('T')[0],
        });
      }
      setSelectedCatalogItems([]);
      setCatalogSearchText('');
      setFurnitureModalTab('catalog');
      setShowFurnitureModal(false);
      toast.success(`Added ${selectedCatalogItems.length} furniture item(s) successfully`);
    } catch (error) {
      toast.error(t('properties:toast.furnitureAddFailed'));
    }
  };

  const handleApplyTemplateInModal = (template) => {
    if (!selectedRoom) return;
    applyTemplateMutation.mutate({
      template_id: template.id,
      room_id: selectedRoom.id
    });
    setShowFurnitureModal(false);
    setFurnitureModalTab('catalog');
  };

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header with Tabs */}
      <PageTitle
        title={t('properties:title')}
        subtitle={t('properties:subtitle')}
        icon={<Icons.Building />}
      />
      <div className="flex p-1 bg-stone-100 rounded-xl">
        <button
          onClick={() => setActiveTab('properties')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
            activeTab === 'properties'
              ? 'bg-white text-teal-700 shadow-sm'
              : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
          }`}
        >
          <Icons.Building size={18} />
          {t('properties:tab.properties')}
        </button>
        <button
          onClick={() => setActiveTab('catalog')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
            activeTab === 'catalog'
              ? 'bg-white text-teal-700 shadow-sm'
              : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
          }`}
        >
          <Icons.Cube size={18} />
          {t('properties:tab.catalog')}
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
            activeTab === 'templates'
              ? 'bg-white text-teal-700 shadow-sm'
              : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
          }`}
        >
          <Icons.FileCheck size={18} />
          {t('properties:tab.templates')}
        </button>
      </div>

      {/* Properties Tab */}
      {activeTab === 'properties' && (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Buildings Panel */}
        <Card rounded="xl">
          <div className="p-4 border-b border-stone-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-stone-900">{t('properties:panel.buildings')}</h2>
              <Button
                kind="primary"
                size="sm"
                iconLeft={<Icons.Plus size={14} />}
                onClick={() => { setEditingBuilding(null); resetBuildingForm(); setShowBuildingModal(true); }}
              >
                Add
              </Button>
            </div>
          </div>
          <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
            {buildingsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="p-3 rounded-xl border border-stone-200">
                    <div className="skeleton h-5 w-32 mb-2"></div>
                    <div className="skeleton h-4 w-48 mb-1"></div>
                    <div className="skeleton h-3 w-24"></div>
                  </div>
                ))}
              </div>
            ) : buildings.length === 0 ? (
              <div className="text-center py-8">
                <Icons.Building size={40} className="mx-auto text-stone-300 mb-2" />
                <p className="text-sm font-medium text-stone-700">No buildings yet</p>
                <p className="text-xs text-stone-500">Add your first building to get started</p>
              </div>
            ) : (
              buildings.map((building) => (
                <div
                  key={building.id}
                  className={`p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                    selectedBuilding?.id === building.id
                      ? 'bg-teal-50 border-2 border-teal-500 shadow-sm'
                      : 'hover:bg-stone-50 border border-stone-200'
                  }`}
                  onClick={() => setSelectedBuilding(building)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-stone-900">{building.name}</h3>
                      <p className="text-sm text-stone-500">{building.address}</p>
                      <p className="text-xs text-stone-400 mt-1">
                        {building.floors} floors, {building.room_count ?? building.total_rooms ?? 0} rooms
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditBuilding(building); }}
                        className="p-1.5 hover:bg-stone-200 rounded-lg transition-colors"
                      >
                        <Icons.Edit size={14} className="text-stone-500" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm('Delete this building?')) deleteBuildingMutation.mutate(building.id);
                        }}
                        className="p-1.5 hover:bg-red-100 rounded-lg transition-colors text-red-600"
                      >
                        <Icons.Trash size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Rooms Panel */}
        <Card rounded="xl">
          <div className="p-4 border-b border-stone-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-stone-900">
                Rooms {selectedBuilding && <span className="text-teal-600">- {selectedBuilding.name}</span>}
              </h2>
              {selectedBuilding && (
                <div className="flex gap-2">
                  <Button
                    kind="secondary"
                    size="sm"
                    iconLeft={<Icons.Wallet size={14} />}
                    onClick={() => { setBulkPricingForm({ monthly_rent: '', apply_to_status: [], floor: '' }); setSelectedRoomIds([]); setShowBulkPricingModal(true); }}
                  >
                    Bulk Pricing
                  </Button>
                  <Button
                    kind="primary"
                    size="sm"
                    iconLeft={<Icons.Plus size={14} />}
                    onClick={() => { setEditingRoom(null); resetRoomForm(); setShowRoomModal(true); }}
                  >
                    Add
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
            {!selectedBuilding ? (
              <div className="text-center py-8">
                <Icons.Home size={40} className="mx-auto text-stone-300 mb-2" />
                <p className="text-sm font-medium text-stone-700">Select a building</p>
                <p className="text-xs text-stone-500">Choose a building to view its rooms</p>
              </div>
            ) : roomsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="p-3 rounded-xl border border-stone-200">
                    <div className="skeleton h-5 w-24 mb-2"></div>
                    <div className="skeleton h-4 w-32 mb-1"></div>
                    <div className="skeleton h-5 w-16 rounded-full"></div>
                  </div>
                ))}
              </div>
            ) : rooms.length === 0 ? (
              <div className="text-center py-8">
                <Icons.Home size={40} className="mx-auto text-stone-300 mb-2" />
                <p className="text-sm font-medium text-stone-700">No rooms yet</p>
                <p className="text-xs text-stone-500">Add rooms to this building</p>
              </div>
            ) : (
              rooms.map((room) => (
                <div
                  key={room.id}
                  className={`p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                    selectedRoom?.id === room.id
                      ? 'bg-teal-50 border-2 border-teal-500 shadow-sm'
                      : 'hover:bg-stone-50 border border-stone-200'
                  }`}
                  onClick={() => setSelectedRoom(room)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-stone-900">Room {room.room_number}</h3>
                      <p className="text-sm text-stone-500">
                        Floor {room.floor} • ₫{room.monthly_rent?.toLocaleString('vi-VN')}/month
                      </p>
                      <Badge tone={room.status === 'available' ? 'success' : room.status === 'occupied' ? 'danger' : 'warning'} className="mt-1">
                        {room.status}
                      </Badge>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditRoom(room); }}
                        className="p-1.5 hover:bg-stone-200 rounded-lg transition-colors"
                      >
                        <Icons.Edit size={14} className="text-stone-500" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm('Delete this room?')) deleteRoomMutation.mutate({ roomId: room.id, buildingId: selectedBuilding?.id });
                        }}
                        className="p-1.5 hover:bg-red-100 rounded-lg transition-colors text-red-600"
                      >
                        <Icons.Trash size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Furniture Panel */}
        <Card rounded="xl">
          <div className="p-4 border-b border-stone-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-stone-900">
                Furniture {selectedRoom && <span className="text-teal-600">- Room {selectedRoom.room_number}</span>}
              </h2>
              {selectedRoom && (
                <Button
                  kind="primary"
                  size="sm"
                  iconLeft={<Icons.Plus size={14} />}
                  onClick={() => {
                    setEditingFurniture(null);
                    resetFurnitureForm();
                    setSelectedCatalogItems([]);
                    setCatalogSearchText('');
                    setFurnitureModalTab('catalog');
                    setShowFurnitureModal(true);
                  }}
                >
                  Add
                </Button>
              )}
            </div>
          </div>
          <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
            {!selectedRoom ? (
              <div className="text-center py-8">
                <Icons.Cube size={48} className="mx-auto mb-2 text-stone-300" />
                <h3 className="text-base font-medium text-stone-500">{t('properties:panel.selectRoom')}</h3>
                <p className="text-sm text-stone-400">Choose a room to view its furniture</p>
              </div>
            ) : furnitureLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="p-3 rounded-xl border border-stone-200 animate-pulse">
                    <div className="h-5 w-32 bg-stone-200 rounded mb-2"></div>
                    <div className="h-4 w-24 bg-stone-200 rounded"></div>
                  </div>
                ))}
              </div>
            ) : furniture.length === 0 ? (
              <div className="text-center py-8">
                <Icons.Cube size={48} className="mx-auto mb-2 text-stone-300" />
                <h3 className="text-base font-medium text-stone-500">{t('properties:panel.noFurniture')}</h3>
                <p className="text-sm text-stone-400">Add furniture to this room</p>
              </div>
            ) : (
              furniture.map((item) => (
                <div
                  key={item.id}
                  className={`p-3 rounded-xl transition-all duration-200 border ${
                    item.open_tickets > 0
                      ? 'border-red-300 bg-red-50 hover:bg-red-100'
                      : 'border-stone-200 hover:bg-stone-50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-stone-900">{item.name}</h3>
                        {item.open_tickets > 0 && (
                          <Badge tone="danger">
                            <Icons.Alert size={12} className="mr-1" />
                            {item.open_tickets} issue{item.open_tickets > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-stone-500">
                        Qty: {item.quantity} • Condition: {item.condition}
                      </p>
                      {item.purchase_price && (
                        <p className="text-xs text-stone-400">
                          Value: ₫{Number(item.purchase_price).toLocaleString('vi-VN')}
                          {item.years_of_use && ` • ${item.years_of_use} years old`}
                        </p>
                      )}
                      {item.total_tickets > 0 && (
                        <p className="text-xs text-stone-400 mt-1 flex items-center gap-1">
                          <Icons.Ticket size={12} />
                          {item.total_tickets} total ticket{item.total_tickets > 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEditFurniture(item)}
                        className="p-1 hover:bg-stone-200 rounded"
                      >
                        <Icons.Edit size={16} />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Delete this furniture?')) {
                            deleteFurnitureMutation.mutate(item.id);
                          }
                        }}
                        className="p-1 hover:bg-red-100 rounded text-red-600"
                      >
                        <Icons.Trash size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Room Detail Modal */}
      {selectedRoom && (
        <div className="modal-backdrop">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
          {/* Modal Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-teal-600 to-teal-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Icons.Home size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Room {selectedRoom.room_number} Details
                  </h2>
                  <p className="text-teal-100 text-sm">
                    {selectedBuilding?.name} • Floor {selectedRoom.floor}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {(() => {
                  // Use status from roomDetails (contract-aware) if loaded, fallback to selectedRoom
                  const actualStatus = roomDetailsLoading ? selectedRoom.status : (roomDetails?.status || selectedRoom.status);
                  const ROOM_STATUS_TONE = { available: 'success', occupied: 'info', maintenance: 'warning' };
                  return (
                    <Badge tone={ROOM_STATUS_TONE[actualStatus] || 'neutral'}>
                      {actualStatus?.charAt(0).toUpperCase() + actualStatus?.slice(1)}
                    </Badge>
                  );
                })()}
                <button
                  onClick={() => setSelectedRoom(null)}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  title="Close"
                >
                  <Icons.X size={20} className="text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Quick Actions Bar - At Top */}
          <div className="px-6 py-3 bg-stone-50 border-b flex flex-wrap gap-2">
            <Button
              kind="primary"
              size="sm"
              iconLeft={<Icons.Edit size={14} />}
              onClick={() => handleEditRoom(selectedRoom)}
            >
              Edit Room
            </Button>
            <Button
              kind="warning"
              size="sm"
              iconLeft={<Icons.Plus size={14} />}
              onClick={() => {
                setEditingFurniture(null);
                resetFurnitureForm();
                setSelectedCatalogItems([]);
                setCatalogSearchText('');
                setFurnitureModalTab('catalog');
                setShowFurnitureModal(true);
              }}
            >
              Add Furniture
            </Button>
            {selectedRoom.status !== 'maintenance' && (
              <Button
                kind="secondary"
                size="sm"
                iconLeft={<Icons.Cog size={14} />}
                onClick={() => {
                  if (confirm('Set room to maintenance mode?')) {
                    updateRoomMutation.mutate({
                      id: selectedRoom.id,
                      data: { ...selectedRoom, status: 'maintenance' },
                      buildingId: selectedBuilding?.id
                    });
                  }
                }}
              >
                Set Maintenance
              </Button>
            )}
            {selectedRoom.status === 'maintenance' && (
              <Button
                kind="success"
                size="sm"
                iconLeft={<Icons.CheckCircle2 size={14} />}
                onClick={() => {
                  if (confirm('Set room as available?')) {
                    updateRoomMutation.mutate({
                      id: selectedRoom.id,
                      data: { ...selectedRoom, status: 'available' },
                      buildingId: selectedBuilding?.id
                    });
                  }
                }}
              >
                Mark Available
              </Button>
            )}
          </div>

          {/* Modal Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {roomDetailsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Icons.Spinner size={32} className="animate-spin text-teal-600" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Current Tenant Info - Show when room has active contract */}
                {roomDetails?.contract && (
                  <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                    <h3 className="text-sm font-bold text-teal-700 mb-3 flex items-center gap-2">
                      <Icons.User size={16} />
                      Current Tenant
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-teal-500 uppercase">Tenant Name</p>
                        <p className="font-semibold text-teal-900">
                          {(() => {
                            const current = (roomDetails.contract.renters || []).filter(r => !r.moved_out_at);
                            if (current[0]) return `${current[0].first_name} ${current[0].last_name}`;
                            return roomDetails.contract.renter_name || 'N/A';
                          })()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-teal-500 uppercase">Contract #</p>
                        <p className="font-semibold text-teal-900">{roomDetails.contract.contract_number}</p>
                      </div>
                      <div>
                        <p className="text-xs text-teal-500 uppercase">Contract Period</p>
                        <p className="font-medium text-teal-800 text-sm">
                          {new Date(roomDetails.contract.start_date).toLocaleDateString('vi-VN')} - {new Date(roomDetails.contract.end_date).toLocaleDateString('vi-VN')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-teal-500 uppercase">Monthly Rent</p>
                        <p className="font-semibold text-teal-900">₫{roomDetails.contract.monthly_rent?.toLocaleString('vi-VN')}</p>
                      </div>
                    </div>
                    {(() => {
                      const currentTenants = (roomDetails.contract.renters || []).filter(r => !r.moved_out_at);
                      if (currentTenants.length <= 1) return null;
                      return (
                        <div className="mt-3 pt-3 border-t border-teal-200">
                          <p className="text-xs text-teal-500 uppercase mb-2">All Tenants ({currentTenants.length})</p>
                          <div className="flex flex-wrap gap-2">
                            {currentTenants.map((renter, idx) => (
                              <span key={idx} className={`px-2 py-1 rounded-full text-xs ${renter.is_primary ? 'bg-teal-600 text-white' : 'bg-teal-100 text-teal-700'}`}>
                                {renter.first_name} {renter.last_name} {renter.is_primary && '(Primary)'}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-xl border border-teal-200">
                    <p className="text-xs font-medium text-teal-600 uppercase">Monthly Rent</p>
                    <p className="text-xl font-bold text-teal-800">₫{selectedRoom.monthly_rent?.toLocaleString('vi-VN') || 0}</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                    <p className="text-xs font-medium text-green-600 uppercase">Area</p>
                    <p className="text-xl font-bold text-green-800">{selectedRoom.area_sqm || 'N/A'} <span className="text-sm font-normal">sqm</span></p>
                  </div>
                  <div className="bg-gradient-to-br from-violet-50 to-violet-100 p-4 rounded-xl border border-violet-200">
                    <p className="text-xs font-medium text-violet-600 uppercase">Floor</p>
                    <p className="text-xl font-bold text-violet-800">{selectedRoom.floor}</p>
                  </div>
                  <div className="bg-gradient-to-br from-stone-50 to-stone-100 p-4 rounded-xl border border-stone-200">
                    <p className="text-xs font-medium text-stone-600 uppercase">Status</p>
                    {(() => {
                      const actualStatus = roomDetails?.status || selectedRoom.status;
                      return (
                        <p className={`text-lg font-bold capitalize ${
                          actualStatus === 'available' ? 'text-green-700' :
                          actualStatus === 'occupied' ? 'text-teal-700' :
                          actualStatus === 'maintenance' ? 'text-amber-700' : 'text-stone-700'
                        }`}>{actualStatus}</p>
                      );
                    })()}
                  </div>
                </div>

                {/* 3-Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Room & Building Info */}
                  <div className="space-y-4">

                    {/* Building Info */}
                    {selectedBuilding && (
                      <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                        <h3 className="text-sm font-bold text-teal-700 mb-4 flex items-center gap-2">
                          <Icons.Building size={16} />
                          Building Information
                        </h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-teal-500">Name</span>
                            <span className="font-medium text-teal-800">{selectedBuilding.name}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <Icons.MapPin size={16} className="text-teal-400 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-teal-700">{selectedBuilding.address}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-teal-500">Total Floors</span>
                            <span className="font-medium text-teal-800">{selectedBuilding.floors}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-teal-500">Total Rooms</span>
                            <span className="font-medium text-teal-800">{selectedBuilding.room_count ?? selectedBuilding.total_rooms ?? 0}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Building Services */}
                    {selectedBuilding && (() => {
                      const services = selectedBuilding.services_config && Array.isArray(selectedBuilding.services_config)
                        ? selectedBuilding.services_config
                        : defaultServicesConfig;
                      const activeServices = services.filter(s => s.is_active !== false);
                      if (activeServices.length === 0) return null;
                      return (
                        <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                          <h3 className="text-sm font-bold text-amber-700 mb-3 flex items-center gap-2">
                            <Icons.Wallet size={16} />
                            Services & Pricing
                          </h3>
                          <div className="space-y-0 divide-y divide-amber-100">
                            {activeServices.map(service => (
                              <div key={service.service_id} className="flex items-center justify-between py-2">
                                <div className="flex items-center gap-2">
                                  <span className={`w-1.5 h-1.5 rounded-full ${service.category === 'fundamental' ? 'bg-teal-500' : 'bg-violet-400'}`} />
                                  <span className="text-sm text-stone-800">{service.name}</span>
                                </div>
                                <span className="text-sm font-medium text-amber-800">
                                  ₫{(service.unit_price || 0).toLocaleString('vi-VN')}<span className="text-xs text-amber-500 font-normal">/{service.unit || 'unit'}</span>
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Meter Mappings */}
                    {selectedRoom && (() => {
                      const meters = selectedRoom.meter_mappings && typeof selectedRoom.meter_mappings === 'object'
                        ? selectedRoom.meter_mappings : {};
                      const meterEntries = Object.entries(meters);
                      const meteredServices = selectedBuilding?.services_config
                        ? selectedBuilding.services_config.filter(s => s.type === 'metered' && s.is_active !== false)
                        : [];
                      return (
                        <MeterMappingsEditor
                          mappings={meters}
                          readOnly={true}
                          meteredServices={meteredServices.length > 0 ? meteredServices : null}
                        />
                      );
                    })()}
                  </div>

                  {/* Middle Column - Tenant & Contract Info */}
                  <div className="space-y-6">
                    {(roomDetails?.status || selectedRoom.status) === 'occupied' && roomDetails?.contract ? (
                      <>
                        {/* Tenant Card */}
                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-200">
                          <h3 className="text-sm font-bold text-emerald-700 mb-4 flex items-center gap-2">
                            <Icons.User size={16} />
                            Current Tenant
                          </h3>
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
                              {roomDetails.tenant.first_name?.[0]}{roomDetails.tenant.last_name?.[0]}
                            </div>
                            <div>
                              <p className="font-semibold text-stone-900">
                                {roomDetails.tenant.first_name} {roomDetails.tenant.last_name}
                              </p>
                              <p className="text-sm text-stone-500">
                                Tenant since {roomDetails.contract?.start_date ? new Date(roomDetails.contract.start_date).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <Icons.Mail size={16} className="text-emerald-500" />
                              <span className="text-stone-600">{roomDetails.tenant.email || 'No email'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Icons.Phone size={16} className="text-emerald-500" />
                              <span className="text-stone-600">{roomDetails.tenant.phone || 'No phone'}</span>
                            </div>
                          </div>
                          {/* Emergency Contact */}
                          {roomDetails.tenant.emergency_contact_name && (
                            <div className="mt-3 pt-3 border-t border-emerald-200">
                              <p className="text-xs font-semibold text-emerald-600 mb-1.5 flex items-center gap-1">
                                <Icons.Alert size={14} />
                                Emergency Contact
                              </p>
                              <p className="text-sm text-stone-700 font-medium">
                                {roomDetails.tenant.emergency_contact_name}
                                {roomDetails.tenant.emergency_contact_relationship && (
                                  <span className="text-xs text-stone-400 font-normal ml-1 capitalize">({roomDetails.tenant.emergency_contact_relationship})</span>
                                )}
                              </p>
                              {roomDetails.tenant.emergency_contact_phone && (
                                <p className="text-sm text-stone-600">{roomDetails.tenant.emergency_contact_phone}</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Contract Card */}
                        {roomDetails.contract && (
                          <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-5 border border-violet-200">
                            <h3 className="text-sm font-bold text-violet-700 mb-4 flex items-center gap-2">
                              <Icons.Document size={16} />
                              Active Contract
                            </h3>
                            <div className="space-y-3">
                              <div className="flex justify-between">
                                <span className="text-violet-500">Contract ID</span>
                                <span className="font-mono text-sm text-violet-800">#{roomDetails.contract.id}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-violet-500">Status</span>
                                <Badge tone={roomDetails.contract.status === 'active' ? 'success' : roomDetails.contract.status === 'pending' ? 'warning' : 'neutral'}>
                                  {roomDetails.contract.status}
                                </Badge>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-violet-500">Monthly Rent</span>
                                <span className="font-semibold text-violet-800">₫{roomDetails.contract.monthly_rent?.toLocaleString('vi-VN')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-violet-500">Deposit</span>
                                <span className="font-medium text-violet-800">₫{roomDetails.contract.deposit_amount?.toLocaleString('vi-VN')}</span>
                              </div>
                              <div className="pt-3 mt-3 border-t border-violet-200">
                                <div className="flex items-center gap-2 text-sm mb-2">
                                  <Icons.Calendar size={16} className="text-violet-500" />
                                  <span className="text-violet-600">Contract Period</span>
                                </div>
                                <div className="flex items-center justify-between bg-violet-100 rounded-lg p-3">
                                  <div className="text-center">
                                    <p className="text-xs text-violet-500">Start</p>
                                    <p className="font-semibold text-violet-800">
                                      {new Date(roomDetails.contract.start_date).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <Icons.ChevronRight size={20} className="text-violet-400" />
                                  <div className="text-center">
                                    <p className="text-xs text-violet-500">End</p>
                                    <p className="font-semibold text-violet-800">
                                      {new Date(roomDetails.contract.end_date).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (roomDetails?.status || selectedRoom.status) === 'occupied' ? (
                      <div className="bg-stone-50 rounded-xl p-5 border border-stone-200 flex flex-col items-center justify-center py-12">
                        <Icons.User size={48} className="text-stone-300 mb-3" />
                        <p className="text-stone-500 text-center">Loading tenant information...</p>
                      </div>
                    ) : (roomDetails?.status || selectedRoom.status) === 'available' ? (
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200 flex flex-col items-center justify-center py-12">
                        <Icons.CheckCircle2 size={48} className="text-green-400 mb-3" />
                        <p className="text-lg font-semibold text-green-700">Room Available</p>
                        <p className="text-green-600 text-center mt-1">This room is ready for new tenants</p>
                        <p className="text-green-500 text-sm mt-2">Create a contract in Contracts page to assign tenants</p>
                      </div>
                    ) : (roomDetails?.status || selectedRoom.status) === 'maintenance' ? (
                      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-5 border border-amber-200 flex flex-col items-center justify-center py-12">
                        <Icons.Cog size={48} className="text-amber-400 mb-3" />
                        <p className="text-lg font-semibold text-amber-700">Under Maintenance</p>
                        <p className="text-amber-600 text-center mt-1">This room is currently being serviced</p>
                        <Button
                          kind="warning"
                          size="sm"
                          iconLeft={<Icons.Edit size={14} />}
                          onClick={() => handleEditRoom(selectedRoom)}
                          className="mt-4"
                        >
                          Update Status
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-stone-50 rounded-xl p-5 border border-stone-200 flex flex-col items-center justify-center py-12">
                        <Icons.Clock size={48} className="text-stone-300 mb-3" />
                        <p className="text-lg font-semibold text-stone-600">Reserved</p>
                        <p className="text-stone-500 text-center mt-1">This room is reserved</p>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Furniture Summary */}
                  <div className="space-y-6">
                    <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold text-teal-700 flex items-center gap-2">
                          <Icons.Cube size={16} />
                          Furniture Inventory
                        </h3>
                        <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                          {furniture.length} items
                        </span>
                      </div>

                      {furnitureLoading ? (
                        <div className="text-center py-4">
                          <Icons.Spinner size={24} className="animate-spin text-teal-500 mx-auto" />
                        </div>
                      ) : furniture.length === 0 ? (
                        <div className="text-center py-8">
                          <Icons.Cube size={40} className="text-teal-300 mx-auto mb-2" />
                          <p className="text-teal-600">No furniture in this room</p>
                          <Button
                            kind="warning"
                            size="sm"
                            iconLeft={<Icons.Plus size={14} />}
                            onClick={() => {
                              setEditingFurniture(null);
                              resetFurnitureForm();
                              setSelectedCatalogItems([]);
                              setCatalogSearchText('');
                              setFurnitureModalTab('catalog');
                              setShowFurnitureModal(true);
                            }}
                            className="mt-3 mx-auto"
                          >
                            Add Furniture
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {furniture.map((item) => (
                            <div
                              key={item.id}
                              className={`p-3 rounded-lg bg-white border ${
                                item.open_tickets > 0 ? 'border-red-300' : 'border-teal-100'
                              }`}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-stone-800">{item.name}</span>
                                    {item.open_tickets > 0 && (
                                      <Badge tone="danger">
                                        {item.open_tickets} issue{item.open_tickets > 1 ? 's' : ''}
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                                    <span>Qty: {item.quantity}</span>
                                    <span className={`px-1.5 py-0.5 rounded ${
                                      item.condition === 'excellent' ? 'bg-green-100 text-green-700' :
                                      item.condition === 'good' ? 'bg-teal-100 text-teal-700' :
                                      item.condition === 'fair' ? 'bg-amber-100 text-amber-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {item.condition}
                                    </span>
                                    {item.purchase_price && (
                                      <span>₫{(item.purchase_price || 0).toLocaleString('vi-VN')}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleEditFurniture(item)}
                                    className="p-1 hover:bg-teal-100 rounded text-teal-600"
                                  >
                                    <Icons.Edit size={14} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (confirm(`Delete "${item.name}"?`)) {
                                        deleteFurnitureMutation.mutate(item.id);
                                      }
                                    }}
                                    className="p-1 hover:bg-red-100 rounded text-red-500"
                                  >
                                    <Icons.Trash size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Total Value */}
                      {furniture.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-teal-200">
                          <div className="flex justify-between items-center">
                            <span className="text-teal-600">Total Value</span>
                            <span className="text-xl font-bold text-teal-800">
                              ₫{furniture.reduce((sum, item) => sum + (parseFloat(item.purchase_price) || 0) * item.quantity, 0).toLocaleString('vi-VN')}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-2 text-sm">
                            <span className="text-teal-500">Open Issues</span>
                            <span className={`font-semibold ${
                              furniture.reduce((sum, item) => sum + (item.open_tickets || 0), 0) > 0
                                ? 'text-red-600'
                                : 'text-green-600'
                            }`}>
                              {furniture.reduce((sum, item) => sum + (item.open_tickets || 0), 0)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}
      </>
      )}

      {/* Furniture Catalog Tab */}
      {activeTab === 'catalog' && (
        <div className="space-y-6">
          {/* Building Selector */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
              <div>
                <h2 className="text-lg font-semibold text-stone-900">{t('properties:tab.catalog')}</h2>
                <p className="text-sm text-stone-600">{t('properties:empty.selectBuildingForCatalog')}</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <label className={LABEL_CLS}>Building:</label>
                  <select
                    value={catalogBuildingId || ''}
                    onChange={(e) => setCatalogBuildingId(parseInt(e.target.value) || null)}
                    className={`${SELECT_CLS} flex-1 min-w-0`}
                  >
                    <option value="">Select a building...</option>
                    {buildings.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                {catalogBuildingId && (
                  <Button
                    kind="primary"
                    iconLeft={<Icons.Plus size={16} />}
                    onClick={() => {
                      setEditingCatalogItem(null);
                      resetCatalogForm();
                      setShowCatalogModal(true);
                    }}
                  >
                    Add Item
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Catalog Items Grid */}
          {!catalogBuildingId ? (
            <div className="bg-white shadow rounded-lg p-12 text-center">
              <Icons.Cube size={64} className="text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-600 mb-2">{t('properties:panel.selectBuilding')}</h3>
              <p className="text-sm text-stone-500">Choose a building to view and manage its furniture catalog</p>
            </div>
          ) : catalogLoading ? (
            <div className="bg-white shadow rounded-lg p-12 text-center">
              <Icons.Spinner size={48} className="animate-spin text-teal-600 mx-auto" />
              <p className="mt-4 text-stone-600">Loading catalog...</p>
            </div>
          ) : furnitureCatalog.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-12 text-center">
              <Icons.Cube size={64} className="text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-600 mb-2">{t('properties:panel.noCatalogItems')}</h3>
              <p className="text-sm text-stone-500 mb-4">Add furniture items to this building's catalog</p>
              <Button
                kind="primary"
                iconLeft={<Icons.Plus size={16} />}
                onClick={() => {
                  setEditingCatalogItem(null);
                  resetCatalogForm();
                  setShowCatalogModal(true);
                }}
              >
                Add First Item
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {furnitureCatalog.map((item) => (
                <div key={item.id} className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-stone-800">{item.name}</h3>
                      {item.category && (
                        <Badge tone="primary" className="capitalize">{item.category}</Badge>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-sm text-stone-500 line-clamp-2 mb-2">{item.description}</p>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                      <span className="font-bold text-teal-600">
                        ₫{(item.default_price || 0).toLocaleString('vi-VN')}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditCatalogItem(item)}
                          className="p-1.5 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded"
                          title="Edit"
                        >
                          <Icons.Edit size={16} />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(t('properties:confirm.deleteCatalogItem'))) {
                              deleteCatalogItemMutation.mutate(item.id);
                            }
                          }}
                          className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Icons.Trash size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Catalog Item Modal */}
      {showCatalogModal && (
        <div className="modal-backdrop">
          <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Icons.Cube size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {editingCatalogItem ? 'Edit Catalog Item' : 'Add Catalog Item'}
                  </h2>
                  <p className="text-teal-100 text-sm">
                    {buildings.find(b => b.id === catalogBuildingId)?.name || 'Building'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowCatalogModal(false);
                  setEditingCatalogItem(null);
                  resetCatalogForm();
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <Icons.X size={24} className="text-white" />
              </button>
            </div>

            <form onSubmit={handleSubmitCatalogItem} className="p-6 space-y-4">
              <div>
                <label className={LABEL_CLS}>Item Name *</label>
                <input
                  type="text"
                  required
                  value={catalogForm.name}
                  onChange={(e) => setCatalogForm({...catalogForm, name: e.target.value})}
                  className={INPUT_CLS}
                  placeholder="e.g., Single Bed, Office Chair"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLS}>Default Price</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={catalogForm.default_price}
                    onChange={(e) => setCatalogForm({...catalogForm, default_price: e.target.value})}
                    className={INPUT_CLS}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className={LABEL_CLS}>Category</label>
                  <select
                    value={catalogForm.category}
                    onChange={(e) => setCatalogForm({...catalogForm, category: e.target.value})}
                    className={SELECT_CLS}
                  >
                    <option value="">Select category</option>
                    <option value="bedroom">Bedroom</option>
                    <option value="living">Living Room</option>
                    <option value="kitchen">Kitchen</option>
                    <option value="bathroom">Bathroom</option>
                    <option value="storage">Storage</option>
                    <option value="appliance">Appliance</option>
                    <option value="fixture">Fixture</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={LABEL_CLS}>Description</label>
                <textarea
                  value={catalogForm.description}
                  onChange={(e) => setCatalogForm({...catalogForm, description: e.target.value})}
                  className={`${INPUT_CLS} h-auto min-h-[4rem]`}
                  rows="2"
                  placeholder="Optional description..."
                />
              </div>

              <div>
                <label className={LABEL_CLS}>Image URL</label>
                <input
                  type="url"
                  value={catalogForm.image_url}
                  onChange={(e) => setCatalogForm({...catalogForm, image_url: e.target.value})}
                  className={INPUT_CLS}
                  placeholder="https://..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  kind="secondary"
                  type="button"
                  onClick={() => {
                    setShowCatalogModal(false);
                    setEditingCatalogItem(null);
                    resetCatalogForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  type="submit"
                  loading={createCatalogItemMutation.isPending || updateCatalogItemMutation.isPending}
                >
                  {editingCatalogItem ? 'Update Item' : 'Add Item'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Furniture Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          {/* Templates Header */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-stone-900">{t('properties:tab.templates')}</h2>
                <p className="text-sm text-stone-600">Create reusable furniture sets that can be applied to rooms</p>
              </div>
              <Button
                kind="primary"
                iconLeft={<Icons.Plus size={16} />}
                onClick={() => {
                  setEditingTemplate(null);
                  resetTemplateForm();
                  setShowTemplateModal(true);
                }}
              >
                Create Template
              </Button>
            </div>
          </div>

          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templatesLoading ? (
              <div className="col-span-full text-center py-12">
                <Icons.Spinner size={48} className="animate-spin text-teal-600 mx-auto" />
                <p className="mt-4 text-stone-600">Loading templates...</p>
              </div>
            ) : furnitureTemplates.length === 0 ? (
              <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
                <Icons.FileCheck size={48} className="text-stone-400 mx-auto" />
                <p className="mt-4 text-stone-600">No furniture templates yet</p>
                <p className="text-sm text-stone-500">Create a template to get started</p>
              </div>
            ) : (
              furnitureTemplates.map((template) => (
                <div key={template.id} className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-stone-900">{template.name}</h3>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {template.room_type && (
                            <Badge tone="info">{template.room_type}</Badge>
                          )}
                          {template.building_name && (
                            <Badge tone="primary">{template.building_name}</Badge>
                          )}
                        </div>
                      </div>
                      <Badge tone={template.is_active ? 'success' : 'neutral'}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    {template.description && (
                      <p className="mt-2 text-sm text-stone-600 line-clamp-2">{template.description}</p>
                    )}

                    <div className="mt-4 border-t pt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-stone-500">
                          Items: {(template.items?.length || 0) + (template.catalog_items?.length || 0)}
                        </span>
                        <span className="font-semibold text-green-600">
                          ₫{(template.total_estimated_value || 0).toLocaleString('vi-VN')}
                        </span>
                      </div>
                      <div className="text-xs text-stone-500 mt-1">
                        Used {template.usage_count || 0} times
                      </div>
                    </div>

                    {/* Items Preview */}
                    {((template.items && template.items.length > 0) || (template.catalog_items && template.catalog_items.length > 0)) && (
                      <div className="mt-4 space-y-1">
                        <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Items:</p>
                        <div className="flex flex-wrap gap-1">
                          {template.catalog_items?.slice(0, 4).map((ci, idx) => (
                            <span key={`ci-${idx}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-teal-100 text-teal-700">
                              {ci.catalog_item_name || ci.name} {ci.quantity > 1 && `(${ci.quantity})`}
                            </span>
                          ))}
                          {template.items?.slice(0, 4 - (template.catalog_items?.length || 0)).map((item, idx) => (
                            <span key={`item-${idx}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-stone-100 text-stone-700">
                              {item.name} {item.quantity > 1 && `(${item.quantity})`}
                            </span>
                          ))}
                          {((template.items?.length || 0) + (template.catalog_items?.length || 0)) > 4 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-stone-200 text-stone-600">
                              +{((template.items?.length || 0) + (template.catalog_items?.length || 0)) - 4} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="px-6 py-3 bg-stone-50 flex justify-between items-center">
                    <button
                      onClick={() => {
                        setSelectedTemplateForApply(template);
                        setShowApplyTemplateModal(true);
                      }}
                      className="flex items-center text-sm text-teal-600 hover:text-teal-800"
                    >
                      <Icons.Download size={16} className="mr-1" />
                      Apply to Room
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="p-1 text-stone-500 hover:text-teal-600"
                        title="Edit"
                      >
                        <Icons.Edit size={16} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(t('properties:confirm.deleteTemplate'))) {
                            deleteTemplateMutation.mutate(template.id);
                          }
                        }}
                        className="p-1 text-stone-500 hover:text-red-600"
                        title="Delete"
                      >
                        <Icons.Trash size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="modal-backdrop">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="bg-gradient-to-r from-violet-600 to-teal-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Icons.FileCheck size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {editingTemplate ? 'Edit Template' : 'Create Furniture Template'}
                  </h2>
                  <p className="text-violet-100 text-sm">Define furniture items for this template</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowTemplateModal(false);
                  setEditingTemplate(null);
                  resetTemplateForm();
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <Icons.X size={24} className="text-white" />
              </button>
            </div>

            <form onSubmit={handleSubmitTemplate} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* Template Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="form-label">Template Name *</label>
                    <input
                      type="text"
                      required
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                      className="form-input"
                      placeholder="e.g., Basic Single Room"
                    />
                  </div>
                  <div>
                    <label className="form-label">Room Type</label>
                    <select
                      value={templateForm.room_type}
                      onChange={(e) => setTemplateForm({...templateForm, room_type: e.target.value})}
                      className="form-input"
                    >
                      <option value="">Any room type</option>
                      <option value="single">Single</option>
                      <option value="double">Double</option>
                      <option value="studio">Studio</option>
                      <option value="suite">Suite</option>
                      <option value="unfurnished">Unfurnished</option>
                    </select>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={templateForm.is_active}
                        onChange={(e) => setTemplateForm({...templateForm, is_active: e.target.checked})}
                        className="w-4 h-4 accent-teal-600 rounded"
                      />
                      <span className="ml-2 text-sm text-stone-700">Template is active</span>
                    </label>
                  </div>
                  <div className="col-span-2">
                    <label className="form-label">Description</label>
                    <textarea
                      value={templateForm.description}
                      onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                      className="form-input"
                      rows="2"
                      placeholder="Describe this furniture template..."
                    />
                  </div>
                </div>

                {/* Building Selection for Catalog Items */}
                <div className="border rounded-lg p-4 bg-teal-50">
                  <h3 className="text-sm font-medium text-teal-800 mb-3">Building (for Catalog Items)</h3>
                  <select
                    value={templateBuildingId || ''}
                    onChange={(e) => setTemplateBuildingId(parseInt(e.target.value) || null)}
                    className={SELECT_CLS}
                  >
                    <option value="">Select a building (optional)</option>
                    {buildings.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-teal-600 mt-1">Select a building to add items from its furniture catalog</p>
                </div>

                {/* Catalog Items Selection */}
                {templateBuildingId && templateCatalogItems.length > 0 && (
                  <div className="border rounded-lg p-4 bg-emerald-50">
                    <h3 className="text-sm font-medium text-emerald-800 mb-3">
                      Add from Catalog ({buildings.find(b => b.id === templateBuildingId)?.name})
                    </h3>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {templateCatalogItems.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => addCatalogItemToTemplate(item)}
                          className="inline-flex items-center px-3 py-1.5 text-sm bg-white border border-emerald-200 rounded-lg hover:bg-emerald-100 hover:border-emerald-400 transition-colors"
                        >
                          <span className="font-medium text-emerald-800">{item.name}</span>
                          <span className="ml-2 text-emerald-600 text-xs">₫{(item.default_price || 0).toLocaleString('vi-VN')}</span>
                          <Icons.Plus size={16} className="ml-1 text-emerald-500" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Catalog Items */}
                {templateForm.catalog_items.length > 0 && (
                  <div className="border rounded-lg p-4 bg-teal-50">
                    <h3 className="text-sm font-medium text-teal-800 mb-3">
                      Selected Catalog Items ({templateForm.catalog_items.length})
                    </h3>
                    <div className="space-y-2">
                      {templateForm.catalog_items.map((ci) => (
                        <div key={ci.catalog_item_id} className="flex items-center justify-between bg-white p-2 rounded-lg border border-teal-200">
                          <span className="font-medium text-stone-700">{ci.catalog_item_name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-stone-500">₫{(ci.default_price || 0).toLocaleString('vi-VN')}</span>
                            <input
                              type="number"
                              min="1"
                              value={ci.quantity}
                              onChange={(e) => updateCatalogItemQuantity(ci.catalog_item_id, e.target.value)}
                              className="w-16 border border-stone-300 rounded px-2 py-1 text-sm text-center focus:outline-none focus:border-teal-600"
                            />
                            <button
                              type="button"
                              onClick={() => removeCatalogItemFromTemplate(ci.catalog_item_id)}
                              className="p-1 text-red-500 hover:bg-red-100 rounded"
                            >
                              <Icons.Trash size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                      <div className="text-right text-sm font-medium text-teal-700 pt-2 border-t border-teal-200">
                        Catalog Total: ₫{templateForm.catalog_items.reduce((sum, ci) => sum + ((ci.default_price || 0) * ci.quantity), 0).toLocaleString('vi-VN')}
                      </div>
                    </div>
                  </div>
                )}

                {/* Legacy Add Item Section (manual entry) */}
                <div className="border rounded-lg p-4 bg-stone-50">
                  <h3 className="text-sm font-medium text-stone-700 mb-3">Add Custom Item (Manual)</h3>
                  <div className="grid grid-cols-6 gap-3">
                    <div className="col-span-2">
                      <input
                        type="text"
                        placeholder="Item name *"
                        value={templateItemForm.name}
                        onChange={(e) => setTemplateItemForm({...templateItemForm, name: e.target.value})}
                        className={INPUT_CLS}
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        min="1"
                        placeholder="Qty"
                        value={templateItemForm.quantity}
                        onChange={(e) => setTemplateItemForm({...templateItemForm, quantity: e.target.value})}
                        className={INPUT_CLS}
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Price ₫"
                        value={templateItemForm.estimated_price}
                        onChange={(e) => setTemplateItemForm({...templateItemForm, estimated_price: e.target.value})}
                        className={INPUT_CLS}
                      />
                    </div>
                    <div>
                      <select
                        value={templateItemForm.category}
                        onChange={(e) => setTemplateItemForm({...templateItemForm, category: e.target.value})}
                        className={SELECT_CLS}
                      >
                        <option value="">Category</option>
                        <option value="bedroom">Bedroom</option>
                        <option value="living">Living</option>
                        <option value="dining">Dining</option>
                        <option value="kitchen">Kitchen</option>
                        <option value="bathroom">Bathroom</option>
                        <option value="storage">Storage</option>
                        <option value="appliance">Appliance</option>
                        <option value="fixture">Fixture</option>
                        <option value="furniture">Furniture</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Button kind="primary" type="button" onClick={addTemplateItem} className="w-full">
                        Add
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Items List */}
                <div>
                  <h3 className="text-sm font-medium text-stone-700 mb-3">
                    Template Items ({templateForm.items.length})
                  </h3>
                  {templateForm.items.length === 0 ? (
                    <p className="text-stone-500 text-sm text-center py-4">No items added yet</p>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-stone-200">
                        <thead className="bg-stone-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Name</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Qty</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Price</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Category</th>
                            <th className="px-4 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-stone-200">
                          {templateForm.items.map((item, idx) => (
                            <tr key={idx}>
                              <td className="px-4 py-2 text-sm text-stone-900">{item.name}</td>
                              <td className="px-4 py-2 text-sm text-stone-500">{item.quantity}</td>
                              <td className="px-4 py-2 text-sm text-stone-500">₫{(item.estimated_price || 0).toLocaleString('vi-VN')}</td>
                              <td className="px-4 py-2 text-sm text-stone-500 capitalize">{item.category || '-'}</td>
                              <td className="px-4 py-2">
                                <button
                                  type="button"
                                  onClick={() => removeTemplateItem(idx)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Icons.Trash size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-stone-50">
                          <tr>
                            <td colSpan="2" className="px-4 py-2 text-sm font-medium text-stone-700">Total Value:</td>
                            <td colSpan="3" className="px-4 py-2 text-sm font-semibold text-green-600">
                              ₫{templateForm.items.reduce((sum, item) => sum + (parseFloat(item.estimated_price) || 0) * (parseInt(item.quantity) || 1), 0).toLocaleString('vi-VN')}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t bg-stone-50 flex justify-end gap-3">
                <Button
                  kind="secondary"
                  type="button"
                  onClick={() => {
                    setShowTemplateModal(false);
                    setEditingTemplate(null);
                    resetTemplateForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  type="submit"
                  loading={createTemplateMutation.isPending || updateTemplateMutation.isPending}
                >
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Building Modal */}
      {showBuildingModal && (
        <div className="modal-backdrop">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Icons.Building size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {editingBuilding ? 'Edit Building' : 'Create New Building'}
                  </h2>
                  <p className="text-teal-100 text-sm">
                    {editingBuilding ? 'Update building information' : 'Set up a new building with floors and rooms'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowBuildingModal(false);
                  setEditingBuilding(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <Icons.X size={20} className="text-white" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <form onSubmit={handleSubmitBuilding}>
                <div className="space-y-6">
                  {/* Basic Info Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={LABEL_CLS}>
                        Building Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={buildingForm.name}
                        onChange={(e) => setBuildingForm({...buildingForm, name: e.target.value})}
                        className={INPUT_CLS}
                        placeholder="e.g., Building A, Sunrise Tower"
                        required
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={buildingForm.address}
                        onChange={(e) => setBuildingForm({...buildingForm, address: e.target.value})}
                        className={INPUT_CLS}
                        placeholder="e.g., 123 Main Street"
                        required
                      />
                    </div>
                  </div>

                  {/* Service Configuration Section (NEW: Unified Schema) */}
                  <ServiceConfigManager
                    services={buildingForm.services_config || defaultServicesConfig}
                    onChange={(updatedServices) => setBuildingForm({ ...buildingForm, services_config: updatedServices })}
                  />

                  {/* Payment Method Configuration */}
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-5 rounded-xl border border-emerald-200">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <Icons.Wallet size={20} className="text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-stone-800">Payment Method</h3>
                        <p className="text-xs text-stone-500">When should renters pay their monthly bills?</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <label className={`relative flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        buildingForm.payment_method === 'pay_first'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-stone-200 bg-white hover:border-emerald-300'
                      }`}>
                        <input
                          type="radio"
                          name="payment_method"
                          value="pay_first"
                          checked={buildingForm.payment_method === 'pay_first'}
                          onChange={(e) => setBuildingForm({ ...buildingForm, payment_method: e.target.value })}
                          className="sr-only"
                        />
                        <span className="text-sm font-semibold text-stone-800">Pay First</span>
                        <span className="text-xs text-stone-500 mt-1">
                          Pay at the start of the rental period. E.g., contract starts March 3rd → pay on March 3rd
                        </span>
                        {buildingForm.payment_method === 'pay_first' && (
                          <Icons.CheckCircle2 size={20} className="absolute top-2 right-2 text-emerald-500" />
                        )}
                      </label>
                      <label className={`relative flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        buildingForm.payment_method === 'pay_later'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-stone-200 bg-white hover:border-emerald-300'
                      }`}>
                        <input
                          type="radio"
                          name="payment_method"
                          value="pay_later"
                          checked={buildingForm.payment_method === 'pay_later'}
                          onChange={(e) => setBuildingForm({ ...buildingForm, payment_method: e.target.value })}
                          className="sr-only"
                        />
                        <span className="text-sm font-semibold text-stone-800">Pay Later</span>
                        <span className="text-xs text-stone-500 mt-1">
                          Pay at the start of the next month. E.g., contract starts March 3rd → pay on April 3rd
                        </span>
                        {buildingForm.payment_method === 'pay_later' && (
                          <Icons.CheckCircle2 size={20} className="absolute top-2 right-2 text-emerald-500" />
                        )}
                      </label>
                    </div>
                    <div className="mt-4 space-y-3">
                      <div>
                        <label className={LABEL_CLS}>Billing Day (optional)</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={buildingForm.billing_day || ''}
                            onChange={(e) => setBuildingForm({ ...buildingForm, billing_day: e.target.value ? parseInt(e.target.value) : null })}
                            className="w-24 h-9 px-3 text-sm border border-stone-300 rounded-md focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
                            min="1"
                            max="31"
                            placeholder="Auto"
                          />
                          <span className="text-sm text-stone-500">
                            Day of month all contracts in this building are due. Leave empty to use each contract's start day.
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className={LABEL_CLS}>Grace Period (days)</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={buildingForm.billing_grace_days ?? 3}
                            onChange={(e) => setBuildingForm({ ...buildingForm, billing_grace_days: parseInt(e.target.value) || 0 })}
                            className="w-24 h-9 px-3 text-sm border border-stone-300 rounded-md focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
                            min="0"
                            max="30"
                          />
                          <span className="text-sm text-stone-500">
                            Days after billing day before payment becomes overdue.
                          </span>
                        </div>
                      </div>

                      {editingBuilding && buildingForm.billing_day && (
                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(t('properties:confirm.applyBillingPolicy', {
                                day: buildingForm.billing_day,
                                grace: buildingForm.billing_grace_days ?? 3,
                              }))) {
                                applyBillingPolicyMutation.mutate(editingBuilding.id);
                              }
                            }}
                            disabled={applyBillingPolicyMutation.isPending}
                            className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                          >
                            {applyBillingPolicyMutation.isPending ? (
                              <>
                                <Icons.Spinner size={16} className="animate-spin" />
                                Applying...
                              </>
                            ) : (
                              <>
                                <Icons.Refresh size={16} />
                                Apply billing policy to all active contracts
                              </>
                            )}
                          </button>
                          <p className="mt-1 text-xs text-stone-500">
                            Switches all active contracts in this building to use the building's billing day. New contracts already default to this.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Floor Configuration Section */}
                  <div className="bg-gradient-to-br from-stone-50 to-stone-100 p-5 rounded-xl border border-stone-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-teal-100 rounded-lg">
                          <Icons.Building size={20} className="text-teal-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-stone-800">Number of Floors</h3>
                          <p className="text-xs text-stone-500">How many floors does this building have?</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleFloorsChange(Math.max(0, (parseInt(buildingForm.floors) || 0) - 1))}
                          className="w-10 h-10 flex items-center justify-center bg-white border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors text-stone-600 font-bold"
                          disabled={!buildingForm.floors || parseInt(buildingForm.floors) <= 0}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={buildingForm.floors}
                          onChange={(e) => handleFloorsChange(e.target.value)}
                          className="w-20 px-3 py-2 border border-stone-300 rounded-lg text-center text-lg font-bold focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          min="1"
                          max="50"
                          placeholder="0"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => handleFloorsChange((parseInt(buildingForm.floors) || 0) + 1)}
                          className="w-10 h-10 flex items-center justify-center bg-white border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors text-stone-600 font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Rooms per Floor Configuration */}
                  {buildingForm.rooms_per_floor.length > 0 && (
                    <div className="border border-stone-200 rounded-xl overflow-hidden">
                      <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icons.Home size={20} className="text-white" />
                          <h3 className="text-sm font-bold text-white">
                            Rooms Per Floor Configuration
                          </h3>
                        </div>
                        {/* Quick Fill */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-green-100">Quick fill:</span>
                          <input
                            type="number"
                            value={quickFillRooms}
                            onChange={(e) => setQuickFillRooms(e.target.value)}
                            placeholder="Rooms"
                            className="w-20 px-2 py-1 text-sm border-0 rounded-md focus:ring-2 focus:ring-green-300"
                            min="0"
                            max="100"
                          />
                          <button
                            type="button"
                            onClick={handleQuickFill}
                            disabled={!quickFillRooms}
                            className="px-3 py-1 bg-white text-green-600 text-xs font-semibold rounded-md hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Apply All
                          </button>
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto p-4 bg-white">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {buildingForm.rooms_per_floor.map((floorData, index) => (
                            <div key={index} className="flex items-center gap-3 bg-stone-50 p-3 rounded-lg border border-stone-100 hover:border-green-300 hover:bg-green-50 transition-all">
                              <div className="flex items-center justify-center w-16 h-10 bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-lg text-sm font-bold shadow-sm">
                                F{floorData.floor}
                              </div>
                              <div className="flex-1 flex items-center gap-2">
                                <input
                                  type="number"
                                  value={floorData.rooms}
                                  onChange={(e) => handleRoomsPerFloorChange(index, e.target.value)}
                                  placeholder="0"
                                  className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                                  min="0"
                                  max="100"
                                  required
                                />
                                <span className="text-xs text-stone-500 font-medium">rooms</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Room Notation Configuration */}
                  {!editingBuilding && buildingForm.rooms_per_floor.length > 0 && (
                    <div className="border border-violet-200 rounded-xl overflow-hidden">
                      <div className="bg-gradient-to-r from-violet-500 to-violet-600 px-4 py-3">
                        <h3 className="text-sm font-bold text-white flex items-center gap-2">
                          <Icons.Layers size={20} />
                          Room Notation Configuration
                        </h3>
                        <p className="text-xs text-violet-200 mt-1">Customize how room numbers are generated</p>
                      </div>
                      <div className="p-4 bg-white">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                          <div>
                            <label className={LABEL_CLS}>Prefix</label>
                            <input
                              type="text"
                              value={buildingForm.room_prefix}
                              onChange={(e) => setBuildingForm({...buildingForm, room_prefix: e.target.value})}
                              placeholder="P, R, ..."
                              className={INPUT_CLS}
                              maxLength={10}
                            />
                          </div>
                          <div>
                            <label className={LABEL_CLS}>Include Floor</label>
                            <select
                              value={buildingForm.include_floor_in_room ? 'yes' : 'no'}
                              onChange={(e) => setBuildingForm({...buildingForm, include_floor_in_room: e.target.value === 'yes'})}
                              className={SELECT_CLS}
                            >
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          </div>
                          <div>
                            <label className={LABEL_CLS}>Separator</label>
                            <select
                              value={buildingForm.room_separator}
                              onChange={(e) => setBuildingForm({...buildingForm, room_separator: e.target.value})}
                              className={SELECT_CLS}
                            >
                              <option value="">None</option>
                              <option value="-">Dash (-)</option>
                              <option value=".">Dot (.)</option>
                              <option value="_">Underscore (_)</option>
                            </select>
                          </div>
                          <div>
                            <label className={LABEL_CLS}>Number Padding</label>
                            <select
                              value={buildingForm.room_padding}
                              onChange={(e) => setBuildingForm({...buildingForm, room_padding: parseInt(e.target.value)})}
                              className={SELECT_CLS}
                            >
                              <option value="1">1 digit (1, 2, 3)</option>
                              <option value="2">2 digits (01, 02)</option>
                              <option value="3">3 digits (001, 002)</option>
                            </select>
                          </div>
                          <div>
                            <label className={LABEL_CLS}>Start From</label>
                            <select
                              value={buildingForm.room_start_number}
                              onChange={(e) => setBuildingForm({...buildingForm, room_start_number: parseInt(e.target.value)})}
                              className={SELECT_CLS}
                            >
                              <option value="0">0 (00, 01, 02...)</option>
                              <option value="1">1 (01, 02, 03...)</option>
                            </select>
                          </div>
                        </div>

                        {/* Quick Pattern Examples */}
                        <div className="mt-4 pt-3 border-t border-stone-100">
                          <span className="text-xs text-stone-500">Quick patterns: </span>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {[
                              { label: 'P101, P102', prefix: 'P', floor: true, sep: '', pad: 2, start: 1 },
                              { label: 'R1-01, R1-02', prefix: 'R', floor: true, sep: '-', pad: 2, start: 1 },
                              { label: '101, 102', prefix: '', floor: true, sep: '', pad: 2, start: 1 },
                              { label: '01, 02, 03', prefix: '', floor: false, sep: '', pad: 2, start: 1 },
                              { label: 'Room 1, Room 2', prefix: 'Room ', floor: false, sep: '', pad: 1, start: 1 },
                            ].map((pattern, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => setBuildingForm({
                                  ...buildingForm,
                                  room_prefix: pattern.prefix,
                                  include_floor_in_room: pattern.floor,
                                  room_separator: pattern.sep,
                                  room_padding: pattern.pad,
                                  room_start_number: pattern.start
                                })}
                                className="px-2 py-1 text-xs bg-violet-50 text-violet-700 rounded border border-violet-200 hover:bg-violet-100 transition-colors"
                              >
                                {pattern.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Room Number Preview */}
                  {!editingBuilding && getRoomNumberPreview().length > 0 && (
                    <div className="border border-amber-200 rounded-xl overflow-hidden bg-amber-50">
                      <div className="bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-2">
                        <h3 className="text-sm font-bold text-white flex items-center gap-2">
                          <Icons.Eye size={16} />
                          Room Number Preview
                        </h3>
                      </div>
                      <div className="p-4 max-h-40 overflow-y-auto">
                        <div className="space-y-2">
                          {getRoomNumberPreview().map((floor, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <span className="text-xs font-bold text-amber-700 w-16">Floor {floor.floor}:</span>
                              <div className="flex flex-wrap gap-1">
                                {floor.rooms.map((room, roomIdx) => (
                                  <span
                                    key={roomIdx}
                                    className={`px-2 py-1 text-xs rounded ${
                                      room === '...'
                                        ? 'text-amber-600'
                                        : 'bg-white border border-amber-200 text-amber-800 font-mono'
                                    }`}
                                  >
                                    {room}
                                  </span>
                                ))}
                                <span className="text-xs text-amber-600 ml-1">({floor.total} rooms)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Summary Section */}
                  <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-5 rounded-xl border border-teal-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-teal-100 rounded-xl">
                          <Icons.Cube size={24} className="text-teal-600" />
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-teal-700">Total Rooms</span>
                          <p className="text-xs text-teal-500">{editingBuilding ? 'Current room count' : 'Auto-calculated from floor configuration'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-4xl font-bold text-teal-800">
                          {buildingForm.total_rooms || 0}
                        </span>
                        <p className="text-sm text-teal-500 font-medium">rooms</p>
                      </div>
                    </div>
                    {!editingBuilding && buildingForm.total_rooms > 0 && (
                      <div className="mt-4 pt-4 border-t border-teal-200 flex items-center gap-2">
                        <Icons.Info size={16} className="text-teal-500" />
                        <p className="text-sm text-teal-600">
                          Rooms will be automatically created using your custom notation pattern.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-stone-200">
                  <Button
                    kind="secondary"
                    type="button"
                    onClick={() => {
                      setShowBuildingModal(false);
                      setEditingBuilding(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    kind="primary"
                    type="submit"
                    loading={createBuildingMutation.isPending || updateBuildingMutation.isPending}
                    iconLeft={editingBuilding ? <Icons.Edit size={14} /> : <Icons.Plus size={14} />}
                  >
                    {editingBuilding ? 'Update Building' : 'Create Building'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Room Modal */}
      {showRoomModal && (
        <div className="modal-backdrop">
          <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4 text-stone-900">
                {editingRoom ? 'Edit Room' : 'Add New Room'}
              </h2>
              <form onSubmit={handleSubmitRoom}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={LABEL_CLS}>Room Number</label>
                      <input
                        type="text"
                        value={roomForm.room_number}
                        onChange={(e) => setRoomForm({...roomForm, room_number: e.target.value})}
                        className={INPUT_CLS}
                        required
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Floor</label>
                      <input
                        type="number"
                        value={roomForm.floor}
                        onChange={(e) => setRoomForm({...roomForm, floor: e.target.value})}
                        className={INPUT_CLS}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={LABEL_CLS}>Area (sqm)</label>
                      <input
                        type="number"
                        value={roomForm.area_sqm}
                        onChange={(e) => setRoomForm({...roomForm, area_sqm: e.target.value})}
                        className={INPUT_CLS}
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Monthly Rent</label>
                      <input
                        type="number"
                        value={roomForm.monthly_rent}
                        onChange={(e) => setRoomForm({...roomForm, monthly_rent: e.target.value})}
                        className={INPUT_CLS}
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Status</label>
                    <select
                      value={roomForm.status}
                      onChange={(e) => setRoomForm({...roomForm, status: e.target.value})}
                      className={SELECT_CLS}
                    >
                      <option value="available">Available</option>
                      <option value="occupied">Occupied</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="reserved">Reserved</option>
                    </select>
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Description</label>
                    <textarea
                      value={roomForm.description}
                      onChange={(e) => setRoomForm({...roomForm, description: e.target.value})}
                      className={`${INPUT_CLS} h-auto min-h-[4.5rem]`}
                      rows="3"
                    />
                  </div>

                  {/* Meter Mappings - shown for both create and edit */}
                  {selectedBuilding && (
                    <MeterMappingsEditor
                      mappings={roomForm.meter_mappings || {}}
                      onChange={(updatedMappings) => setRoomForm({...roomForm, meter_mappings: updatedMappings})}
                      meteredServices={
                        (selectedBuilding.services_config && Array.isArray(selectedBuilding.services_config)
                          ? selectedBuilding.services_config
                          : defaultServicesConfig
                        ).filter(s => s.type === 'metered' && s.is_active !== false)
                      }
                    />
                  )}

                  {/* Building Services (read-only) */}
                  {editingRoom && selectedBuilding && (() => {
                    const services = selectedBuilding.services_config && Array.isArray(selectedBuilding.services_config)
                      ? selectedBuilding.services_config
                      : defaultServicesConfig;
                    const activeServices = services.filter(s => s.is_active !== false);
                    if (activeServices.length === 0) return null;
                    return (
                      <div>
                        <label className={LABEL_CLS}>Building Services</label>
                        <div className="bg-stone-50 rounded-lg border border-stone-200 divide-y divide-stone-200">
                          {activeServices.map(service => (
                            <div key={service.service_id} className="flex items-center justify-between px-3 py-2">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${service.category === 'fundamental' ? 'bg-teal-500' : 'bg-violet-400'}`} />
                                <span className="text-sm text-stone-800">{service.name}</span>
                                <span className="text-xs text-stone-400">
                                  {service.type === 'metered' ? 'Metered' : service.type === 'fixed_per_person' ? 'Per person' : service.type === 'fixed_per_room' ? 'Per room' : 'One-time'}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-stone-700">
                                ₫{(service.unit_price || 0).toLocaleString('vi-VN')}<span className="text-xs text-stone-400">/{service.unit || 'unit'}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-stone-400 mt-1">Inherited from building. Edit in Building Settings.</p>
                      </div>
                    );
                  })()}
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <Button
                    kind="secondary"
                    type="button"
                    onClick={() => {
                      setShowRoomModal(false);
                      setEditingRoom(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button kind="primary" type="submit">
                    {editingRoom ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Room Pricing Modal */}
      {showBulkPricingModal && selectedBuilding && (
        <div className="modal-backdrop">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Icons.Wallet size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Bulk Room Pricing</h2>
                  <p className="text-emerald-100 text-sm">{selectedBuilding.name} • {rooms.length} rooms</p>
                </div>
              </div>
              <button
                onClick={() => setShowBulkPricingModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <Icons.X size={24} className="text-white" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                {/* Price Input Row */}
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className={LABEL_CLS}>
                      Monthly Rent (₫) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={bulkPricingForm.monthly_rent}
                      onChange={(e) => setBulkPricingForm({...bulkPricingForm, monthly_rent: e.target.value})}
                      className={INPUT_CLS}
                      placeholder="e.g., 3000000"
                      min="0"
                      step="1000"
                    />
                  </div>
                  <Button
                    kind="success"
                    iconLeft={bulkPricingMutation.isPending ? <Icons.Spinner size={14} className="animate-spin" /> : <Icons.Wallet size={14} />}
                    loading={bulkPricingMutation.isPending}
                    disabled={!bulkPricingForm.monthly_rent || selectedRoomIds.length === 0}
                    onClick={() => {
                      if (!bulkPricingForm.monthly_rent) { toast.error(t('properties:toast.priceRequired')); return; }
                      if (selectedRoomIds.length === 0) { toast.error(t('properties:toast.selectAtLeastOneRoom')); return; }
                      bulkPricingMutation.mutate({ buildingId: selectedBuilding.id, data: { monthly_rent: parseFloat(bulkPricingForm.monthly_rent), room_ids: selectedRoomIds } });
                    }}
                  >
                    Apply to Selected ({selectedRoomIds.length})
                  </Button>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setSelectedRoomIds(rooms.map(r => r.id))}
                    className="text-xs px-2 py-1 bg-stone-100 hover:bg-stone-200 rounded text-stone-700"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedRoomIds([])}
                    className="text-xs px-2 py-1 bg-stone-100 hover:bg-stone-200 rounded text-stone-700"
                  >
                    Deselect All
                  </button>
                  <span className="text-stone-300">|</span>
                  {[...new Set(rooms.map(r => r.floor))].sort((a, b) => a - b).map(floor => (
                    <button
                      key={floor}
                      type="button"
                      onClick={() => {
                        const floorRoomIds = rooms.filter(r => r.floor === floor).map(r => r.id);
                        const allSelected = floorRoomIds.every(id => selectedRoomIds.includes(id));
                        if (allSelected) {
                          setSelectedRoomIds(selectedRoomIds.filter(id => !floorRoomIds.includes(id)));
                        } else {
                          setSelectedRoomIds([...new Set([...selectedRoomIds, ...floorRoomIds])]);
                        }
                      }}
                      className={`text-xs px-2 py-1 rounded ${
                        rooms.filter(r => r.floor === floor).every(r => selectedRoomIds.includes(r.id))
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-stone-100 hover:bg-stone-200 text-stone-700'
                      }`}
                    >
                      Floor {floor}
                    </button>
                  ))}
                </div>

                {/* Room List */}
                <div className="border border-stone-200 rounded-lg overflow-hidden">
                  <div className="bg-stone-50 px-3 py-2 border-b border-stone-200 flex items-center justify-between">
                    <span className="text-sm font-medium text-stone-700">Rooms</span>
                    <span className="text-xs text-stone-500">
                      {selectedRoomIds.length} of {rooms.length} selected
                    </span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {rooms.length === 0 ? (
                      <div className="p-4 text-center text-stone-500">No rooms in this building</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-stone-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left w-10">
                              <input
                                type="checkbox"
                                checked={rooms.length > 0 && selectedRoomIds.length === rooms.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedRoomIds(rooms.map(r => r.id));
                                  } else {
                                    setSelectedRoomIds([]);
                                  }
                                }}
                                className="rounded border-stone-300 accent-emerald-600"
                              />
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-stone-600">Room</th>
                            <th className="px-3 py-2 text-left font-medium text-stone-600">Floor</th>
                            <th className="px-3 py-2 text-left font-medium text-stone-600">Status</th>
                            <th className="px-3 py-2 text-right font-medium text-stone-600">Current Rent</th>
                            <th className="px-3 py-2 text-right font-medium text-stone-600">New Rent</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                          {rooms.map((room) => {
                            const isSelected = selectedRoomIds.includes(room.id);
                            const newRent = bulkPricingForm.monthly_rent ? parseFloat(bulkPricingForm.monthly_rent) : null;
                            const rentDiff = newRent ? newRent - (room.monthly_rent || 0) : 0;
                            return (
                              <tr
                                key={room.id}
                                className={`cursor-pointer transition-colors ${
                                  isSelected ? 'bg-emerald-50' : 'hover:bg-stone-50'
                                }`}
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedRoomIds(selectedRoomIds.filter(id => id !== room.id));
                                  } else {
                                    setSelectedRoomIds([...selectedRoomIds, room.id]);
                                  }
                                }}
                              >
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}}
                                    className="rounded border-stone-300 accent-emerald-600"
                                  />
                                </td>
                                <td className="px-3 py-2 font-medium text-stone-900">{room.room_number}</td>
                                <td className="px-3 py-2 text-stone-600">{room.floor}</td>
                                <td className="px-3 py-2">
                                  <Badge tone={room.status === 'available' ? 'success' : room.status === 'occupied' ? 'danger' : room.status === 'maintenance' ? 'warning' : 'info'}>
                                    {room.status}
                                  </Badge>
                                </td>
                                <td className="px-3 py-2 text-right text-stone-600">
                                  ₫{(room.monthly_rent || 0).toLocaleString('vi-VN')}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {isSelected && newRent ? (
                                    <div>
                                      <span className="font-medium text-emerald-600">
                                        ₫{newRent.toLocaleString('vi-VN')}
                                      </span>
                                      {rentDiff !== 0 && (
                                        <span className={`text-xs ml-1 ${rentDiff > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                          ({rentDiff > 0 ? '+' : ''}{rentDiff.toLocaleString('vi-VN')})
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-stone-400">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Summary */}
                {selectedRoomIds.length > 0 && bulkPricingForm.monthly_rent && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-sm text-emerald-800">
                      <strong>Summary:</strong> Update {selectedRoomIds.length} room(s) to{' '}
                      <strong>₫{parseFloat(bulkPricingForm.monthly_rent).toLocaleString('vi-VN')}/month</strong>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-stone-200 flex justify-end gap-3 flex-shrink-0 bg-stone-50">
              <Button kind="secondary" onClick={() => setShowBulkPricingModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Furniture Modal - Tabbed: Catalog / Template / Manual */}
      {showFurnitureModal && (
        <div className="modal-backdrop">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Icons.Cube size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {editingFurniture ? 'Edit Furniture' : 'Add Furniture'}
                  </h2>
                  <p className="text-teal-100 text-sm">
                    {editingFurniture
                      ? `Editing "${editingFurniture.name}"`
                      : `Room ${selectedRoom?.room_number || ''}`
                    }
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowFurnitureModal(false);
                  setEditingFurniture(null);
                  setSelectedCatalogItems([]);
                  setCatalogSearchText('');
                  setFurnitureModalTab('catalog');
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <Icons.X size={24} className="text-white" />
              </button>
            </div>

            {/* Tabs - only show when adding new (not editing) */}
            {!editingFurniture && (
              <div className="flex border-b border-stone-200 px-6 pt-2 bg-stone-50">
                {[
                  { id: 'catalog', label: 'From Catalog', Icon: Icons.FileCheck },
                  { id: 'template', label: 'From Template', Icon: Icons.Layers },
                  { id: 'manual', label: 'Manual Entry', Icon: Icons.Edit }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setFurnitureModalTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      furnitureModalTab === tab.id
                        ? 'border-teal-500 text-teal-700 bg-white rounded-t-lg'
                        : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
                    }`}
                  >
                    <tab.Icon size={16} />
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">

              {/* === EDIT MODE: Show manual form === */}
              {editingFurniture && (
                <form onSubmit={handleSubmitFurniture}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={LABEL_CLS}>Name</label>
                      <input type="text" value={furnitureForm.name} onChange={(e) => setFurnitureForm({...furnitureForm, name: e.target.value})} className={INPUT_CLS} required />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Quantity</label>
                      <input type="number" value={furnitureForm.quantity} onChange={(e) => setFurnitureForm({...furnitureForm, quantity: e.target.value})} className={INPUT_CLS} min="1" required />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Condition</label>
                      <select value={furnitureForm.condition} onChange={(e) => setFurnitureForm({...furnitureForm, condition: e.target.value})} className={SELECT_CLS}>
                        <option value="excellent">Excellent</option>
                        <option value="good">Good</option>
                        <option value="fair">Fair</option>
                        <option value="poor">Poor</option>
                      </select>
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Purchase Date</label>
                      <input type="date" value={furnitureForm.purchase_date} onChange={(e) => setFurnitureForm({...furnitureForm, purchase_date: e.target.value})} className={INPUT_CLS} />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Purchase Price (₫)</label>
                      <input type="number" value={furnitureForm.purchase_price} onChange={(e) => setFurnitureForm({...furnitureForm, purchase_price: e.target.value})} className={INPUT_CLS} step="1" />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Years of Use</label>
                      <input type="number" value={furnitureForm.years_of_use} onChange={(e) => setFurnitureForm({...furnitureForm, years_of_use: e.target.value})} className={INPUT_CLS} min="0" />
                    </div>
                    <div className="col-span-2">
                      <label className={LABEL_CLS}>Description</label>
                      <textarea value={furnitureForm.description} onChange={(e) => setFurnitureForm({...furnitureForm, description: e.target.value})} className={`${INPUT_CLS} h-auto min-h-[4.5rem]`} rows="3" />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <Button kind="secondary" type="button" onClick={() => { setShowFurnitureModal(false); setEditingFurniture(null); }}>Cancel</Button>
                    <Button kind="primary" type="submit">Update</Button>
                  </div>
                </form>
              )}

              {/* === CATALOG TAB === */}
              {!editingFurniture && furnitureModalTab === 'catalog' && (
                <div className="space-y-4">
                  {/* Search */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search catalog items..."
                      value={catalogSearchText}
                      onChange={(e) => setCatalogSearchText(e.target.value)}
                      className={`${INPUT_CLS} pl-10`}
                    />
                    <Icons.Search size={20} className="absolute left-3 top-2.5 text-stone-400" />
                  </div>

                  {/* Catalog Items Grid */}
                  {modalCatalogLoading ? (
                    <div className="text-center py-8 text-stone-500">Loading catalog...</div>
                  ) : modalCatalogItems.length === 0 ? (
                    <div className="text-center py-8">
                      <Icons.Cube size={48} className="text-stone-300 mx-auto mb-3" />
                      <p className="text-stone-500 font-medium">No catalog items for this building</p>
                      <p className="text-sm text-stone-400 mt-1">Go to the Catalog tab to add items first</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {modalCatalogItems
                        .filter(item => item.is_active !== false)
                        .filter(item => !catalogSearchText || item.name.toLowerCase().includes(catalogSearchText.toLowerCase()) || (item.category || '').toLowerCase().includes(catalogSearchText.toLowerCase()))
                        .map(item => {
                          const isSelected = selectedCatalogItems.some(s => s.id === item.id);
                          return (
                            <div
                              key={item.id}
                              onClick={() => handleAddCatalogItemToSelection(item)}
                              className={`p-3 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                                isSelected
                                  ? 'border-teal-400 bg-teal-50 shadow-sm'
                                  : 'border-stone-200 hover:border-teal-300 bg-white'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-stone-900 text-sm truncate">{item.name}</p>
                                  {item.category && (
                                    <span className="inline-block mt-1 text-xs bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded">{item.category}</span>
                                  )}
                                </div>
                                {isSelected && (
                                  <Icons.CheckCircle2 size={20} className="text-teal-500 flex-shrink-0 ml-1" />
                                )}
                              </div>
                              <p className="text-sm font-semibold text-teal-600 mt-2">
                                ₫{(item.default_price || 0).toLocaleString('vi-VN')}
                              </p>
                              {!isSelected && (
                                <p className="text-xs text-stone-400 mt-1">Click to add</p>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {/* Selected Items List */}
                  {selectedCatalogItems.length > 0 && (
                    <div className="mt-4 border-t pt-4">
                      <h4 className="text-sm font-semibold text-stone-700 mb-3">
                        Selected Items ({selectedCatalogItems.length})
                      </h4>
                      <div className="space-y-2">
                        {selectedCatalogItems.map(item => (
                          <div key={item.id} className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-stone-900 text-sm truncate">{item.name}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-stone-500">Qty:</label>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleUpdateSelectedItemQuantity(item.id, e.target.value)}
                                className="w-16 px-2 py-1 border border-stone-300 rounded text-sm text-center focus:outline-none focus:border-teal-600"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <select
                                value={item.condition}
                                onChange={(e) => handleUpdateSelectedItemCondition(item.id, e.target.value)}
                                className="px-2 py-1 border border-stone-300 rounded text-sm focus:outline-none focus:border-teal-600"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="excellent">Excellent</option>
                                <option value="good">Good</option>
                                <option value="fair">Fair</option>
                                <option value="poor">Poor</option>
                              </select>
                            </div>
                            <span className="text-sm font-medium text-teal-700 w-24 text-right">
                              ₫{((item.default_price || 0) * item.quantity).toLocaleString('vi-VN')}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRemoveCatalogItemFromSelection(item.id); }}
                              className="p-1 hover:bg-red-100 rounded text-red-500"
                            >
                              <Icons.X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-teal-200">
                        <span className="text-sm font-medium text-stone-600">Total Value</span>
                        <span className="text-lg font-bold text-teal-700">
                          ₫{selectedCatalogItems.reduce((sum, item) => sum + (item.default_price || 0) * item.quantity, 0).toLocaleString('vi-VN')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* === TEMPLATE TAB === */}
              {!editingFurniture && furnitureModalTab === 'template' && (
                <div className="space-y-3">
                  {furnitureTemplates.length === 0 ? (
                    <div className="text-center py-8">
                      <Icons.Layers size={48} className="text-stone-300 mx-auto mb-3" />
                      <p className="text-stone-500 font-medium">No templates available</p>
                      <p className="text-sm text-stone-400 mt-1">Go to the Templates tab to create templates first</p>
                    </div>
                  ) : (
                    furnitureTemplates.map(template => {
                      const itemCount = (template.items?.length || 0) + (template.catalog_items?.length || 0);
                      return (
                        <div key={template.id} className="p-4 border-2 border-stone-200 rounded-xl hover:border-green-300 transition-all bg-white">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-stone-900">{template.name}</h4>
                                {template.room_type && (
                                  <Badge tone="info">{template.room_type}</Badge>
                                )}
                              </div>
                              {template.description && (
                                <p className="text-sm text-stone-500 mt-1">{template.description}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-stone-500">
                                <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                                <span className="font-medium text-green-600">₫{(template.total_estimated_value || 0).toLocaleString('vi-VN')}</span>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-1">
                                {(template.items || []).slice(0, 5).map((item, idx) => (
                                  <span key={`legacy-${idx}`} className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded">{item.name}</span>
                                ))}
                                {(template.catalog_items || []).slice(0, 5).map((item, idx) => (
                                  <span key={`cat-${idx}`} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{item.catalog_item_name || item.name}</span>
                                ))}
                                {itemCount > 5 && <span className="text-xs text-stone-400">+{itemCount - 5} more</span>}
                              </div>
                            </div>
                            <Button
                              kind="success"
                              size="sm"
                              iconLeft={<Icons.Download size={14} />}
                              loading={applyTemplateMutation.isPending}
                              onClick={() => {
                                if (confirm(`Apply template "${template.name}" to Room ${selectedRoom?.room_number}? This will create ${itemCount} furniture items.`)) {
                                  handleApplyTemplateInModal(template);
                                }
                              }}
                              className="ml-4 flex-shrink-0"
                            >
                              Apply
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* === MANUAL ENTRY TAB === */}
              {!editingFurniture && furnitureModalTab === 'manual' && (
                <form onSubmit={handleSubmitFurniture}>
                  <p className="text-sm text-stone-500 mb-4">Add a custom furniture item not in the catalog.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={LABEL_CLS}>Name</label>
                      <input type="text" value={furnitureForm.name} onChange={(e) => setFurnitureForm({...furnitureForm, name: e.target.value})} className={INPUT_CLS} required />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Quantity</label>
                      <input type="number" value={furnitureForm.quantity} onChange={(e) => setFurnitureForm({...furnitureForm, quantity: e.target.value})} className={INPUT_CLS} min="1" required />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Condition</label>
                      <select value={furnitureForm.condition} onChange={(e) => setFurnitureForm({...furnitureForm, condition: e.target.value})} className={SELECT_CLS}>
                        <option value="excellent">Excellent</option>
                        <option value="good">Good</option>
                        <option value="fair">Fair</option>
                        <option value="poor">Poor</option>
                      </select>
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Purchase Date</label>
                      <input type="date" value={furnitureForm.purchase_date} onChange={(e) => setFurnitureForm({...furnitureForm, purchase_date: e.target.value})} className={INPUT_CLS} />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Purchase Price (₫)</label>
                      <input type="number" value={furnitureForm.purchase_price} onChange={(e) => setFurnitureForm({...furnitureForm, purchase_price: e.target.value})} className={INPUT_CLS} step="1" />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>Years of Use</label>
                      <input type="number" value={furnitureForm.years_of_use} onChange={(e) => setFurnitureForm({...furnitureForm, years_of_use: e.target.value})} className={INPUT_CLS} min="0" />
                    </div>
                    <div className="col-span-2">
                      <label className={LABEL_CLS}>Description</label>
                      <textarea value={furnitureForm.description} onChange={(e) => setFurnitureForm({...furnitureForm, description: e.target.value})} className={`${INPUT_CLS} h-auto min-h-[4.5rem]`} rows="3" />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <Button kind="secondary" type="button" onClick={() => { setShowFurnitureModal(false); setEditingFurniture(null); }}>Cancel</Button>
                    <Button kind="primary" type="submit">Create</Button>
                  </div>
                </form>
              )}
            </div>

            {/* Footer - only for catalog tab with selections */}
            {!editingFurniture && furnitureModalTab === 'catalog' && selectedCatalogItems.length > 0 && (
              <div className="border-t border-stone-200 px-6 py-4 bg-stone-50 flex justify-between items-center">
                <Button
                  kind="secondary"
                  onClick={() => {
                    setShowFurnitureModal(false);
                    setSelectedCatalogItems([]);
                    setCatalogSearchText('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  onClick={handleAddFromCatalog}
                  loading={createFurnitureMutation.isPending}
                  iconLeft={<Icons.Plus size={14} />}
                >
                  Add Selected ({selectedCatalogItems.length})
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyManagement;